import type { DepositLink } from './deposit-step';
import { lockPage, unlockPage } from './page-lock';
import type {
  Step, WidgetConfig, ClientProfile, ClientSearchResult,
  Booking, Sale, WalletData, ClientPackage, ClientMembership,
  FormSubmission, ApiData, BookingLocation,
  Category, Provider, EnhancementGroup, Service,
  BookingItem, CurrentItem, FormConfig, AvailabilityConfig,
  GiftCardConfig, GiftCardPromotion, TimePeriod, WorkDays,
  PackageTemplate, MembershipPlanPublic, WeekDay, ProcessedEntry, FormField,
  GuestInfo, RecurringSeries, AiMessage,
  ExistingHold, BookingCredit,
} from './types';
import { getSlotsForDate } from './slots';
import { validatePhone, validateEmail } from './validation';
import { TIMING } from './constants';
import { trackWidgetEvent, clearClientSession, fetchAvailability, type SameDayPromo } from './api';
import { serviceVerdict, providerVerdict, enhancementVerdict } from './membership-eligibility';
import { forgetRemembered, forgetRecognition, loadRemembered, saveRemembered } from './device-memory';
import { isCardComplete as stripeCardComplete, isStripeEnabled, createPaymentMethod as stripeCreatePM, cardDiagnostics, isLostCardFieldError } from './stripe';

/** Mirrors api/utils/shift_cadence.py: does an every-N-weeks shift range apply on dateStr? */
function _weekStartMs(dateStr: string): number {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
  return dt.getTime();
}
export function rangeAppliesOn(r: { everyNWeeks?: number; anchorDate?: string; endDate?: string }, dateStr: string): boolean {
  const day = dateStr.slice(0, 10);
  // anchorDate = first day the shift applies (any cadence); endDate = last.
  if (r.anchorDate && day < r.anchorDate.slice(0, 10)) return false;
  if (r.endDate && day > r.endDate.slice(0, 10)) return false;
  const n = Number(r.everyNWeeks || 1);
  if (n <= 1 || !r.anchorDate) return true;
  const weeks = Math.round((_weekStartMs(dateStr) - _weekStartMs(r.anchorDate)) / (7 * 86400000));
  return ((weeks % n) + n) % n === 0;
}


function normalizeBase(apiBase: string): string {
  return apiBase.replace(/\/+$/, '');
}

/** Statuses that are NOT a visit still to come — a value export, so the
 *  renderer (which imports PortalState as a TYPE only) can read it. */
export const NOT_UPCOMING = ['cancelled', 'completed', 'expired', 'noshow', 'no-show', 'waitlist', 'pending_deposit', 'merged'];

/** Union of several providers' workDays: every day any of them works, with
 *  all of their ranges on it. */
export function unionWorkDays(providers: Provider[]): WorkDays {
  const out: WorkDays = {};
  for (const p of providers) {
    for (const [dow, ranges] of Object.entries(p.workDays || {})) {
      if (!ranges?.length) continue;
      (out[dow] ||= []).push(...ranges);
    }
  }
  return out;
}

export class PortalState {
  // ── Spa identity ──────────────────────────────────────────────────────
  spaName: string;
  spaLogoUrl: string | null;
  isStandalone: boolean;
  businessPhone: string = '';
  /** 'call' | 'text' | 'both' — which links the contact strip draws for the number. */
  businessPhoneActions: string = 'call';
  /** The spa's street address on one line ('' when the location has none). */
  businessAddress: string = '';
  apiBase: string = '';

  // ── Entry mode ────────────────────────────────────────────────────────
  entryMode: 'booking' | 'portal' | 'giftcard' = 'booking';
  /** "Remembered on this device": prefill the contact form from the last
   *  booking on this browser (shared record with the same-day quick-book).
   *  Tenant switch: onlineBookingSettings.quickBook.rememberOnDevice. */
  rememberOnDevice = true;
  rememberedFirstName = '';   // non-empty while the contact form is prefilled from the device

  // ── Panel ─────────────────────────────────────────────────────────────
  panelOpen = false;
  step: Step = 'login';
  stepHistory: Step[] = [];

  // ── Login ─────────────────────────────────────────────────────────────
  loginQuery = '';
  loginError: string | null = null;
  loginLoading = false;
  loginResults: ClientSearchResult[] | null = null;
  authMethod: 'otp' | 'birthday' = 'otp';
  loginBirthday = '';
  otpSent = false;
  otpCode = '';
  otpDebugCode: string | null = null;
  otpNotice: string | null = null;
  multiAccountClients: Array<{id: number, name: string, phoneMasked: string}> | null = null;
  multiAccountNote: string | null = null;

  // ── Registration (new client) ──────────────────────────────────────────
  registrationToken: string | null = null;
  regFirstName = '';
  regLastName = '';
  regEmail = '';
  regPhone = '';        // asked only when the sign-in code went to an email — an account needs a phone
  regMarketingEmails = true;
  regMarketingTexts = true;
  regError: string | null = null;
  bkMarketingTexts = false;   // SMS marketing opt-in on the booking confirm step
  regLoading = false;

  // ── Client ────────────────────────────────────────────────────────────
  clientId: number | null = null;
  clientToken: string | null = null;
  client: ClientProfile | null = null;

  // ── Cached portal section data (lazy-loaded) ─────────────────────────
  portalBookings: Booking[] | null = null;
  sales: Sale[] | null = null;
  wallet: WalletData | null = null;
  portalPackages: ClientPackage[] | null = null;
  memberships: ClientMembership[] | null = null;

  // Form submissions
  formSubmissions: FormSubmission[] | null = null;
  formSubmissionsLoading = false;
  selectedFormId: number | null = null;
  selectedFormDetail: FormSubmission | null = null;
  formDetailLoading = false;

  // Rebook
  recentBookings: Booking[] = [];
  rebookSource: Booking | null = null;

  // Detail views
  selectedBookingId: number | null = null;
  selectedSaleId: number | null = null;

  // Recurring appointments
  recurringSeries: RecurringSeries[] | null = null;
  recurringLoading = false;
  selectedSeriesId: number | null = null;
  selectedSeriesDetail: RecurringSeries | null = null;
  seriesDetailLoading = false;

  // Referral program
  referralCode: string = '';
  referralEnabled = false;
  /** The link to hand a friend, and how the program has gone for them (Tim, 2026-11-02). */
  referralShareUrl: string = '';
  /** The share link as a QR code (object URL), once asked for. */
  referralQrUrl: string = '';
  referralQrOpen = false;
  referralCount = 0;
  referralPending = 0;
  referralEarned = 0;
  referralDiscountLabel: string = '';
  referralRewardDescription: string = '';
  /** The program's rules, shown under Refer a Friend (Tim, 2026-11-03). */
  referralTerms: string[] = [];
  referralLoading = false;

  // Punch cards
  punchCards: Array<{ id: number; templateId: number; templateName: string; targetType: string; targetName: string; currentPunches: number; punchesNeeded: number; status: string; rewardType: string; rewardValue: number; rewardServiceName: string }> = [];
  punchCardsLoading = false;

  // Client portal (account login). When false the widget is a pure booking tool:
  // no silent session restore, no dashboard, no sign-in affordances.
  portalEnabled = true;

  // Loyalty program
  loyaltyEnabled = false;
  loyaltyPoints = 0;
  loyaltyLifetimePoints = 0;
  loyaltyAvailableTiers: Array<{ name: string; pointsCost: number; description: string }> = [];
  loyaltyNextTier: { name: string; pointsCost: number } | null = null;
  loyaltyPointsToNextTier = 0;
  loyaltyLoading = false;

  // Calendar sync
  calSyncEnabled = false;
  calSyncFeedUrl: string = '';
  calSyncLoading = false;

  // Web Chat + AI Concierge
  webChatEnabled = false;
  aiConciergeEnabled = false;
  aiConciergeChips: string[] = [];
  aiMessages: AiMessage[] = [];
  aiLoading = false;
  aiInput = '';
  aiConversationId: number | null = null;
  aiSessionKey: string = '';
  aiLastMsgId: number = 0;
  aiPaused: boolean = false;

  // Checkout products
  checkoutProductsEnabled = false;
  checkoutProducts: any[] = [];
  checkoutProductsLoaded = false;
  /** [{id, name, price, quantity}] — the checkout picker's choices, or a shop cart */
  selectedProducts: any[] = [];
  /** open({serviceId}) before the menu has loaded: picked once setBookingData runs */
  pendingServiceId: string | null = null;
  /** 'Book with this package / membership' — the session this booking is
   *  made with. The menu narrows to what it covers, the provider step to who
   *  may deliver it, its service owes no deposit, and the server reserves the
   *  session the moment the booking exists (Tim, 2026-10-14). */
  bookingCredit: BookingCredit | null = null;
  cardOnFileBusy = false;
  cardOnFileError: string | null = null;
  productSearchQuery = '';
  productGroupBy: 'category' | 'brand' = 'category';
  productPickerOpen = false;
  aiPhone: string = '';
  aiPhoneCollapsed: boolean = false;
  aiPhoneSubmitted: boolean = false;
  _aiPollTimer: any = null;

  // Section loading flags
  bookingsLoading = false;
  salesLoading = false;
  walletLoading = false;

  // ── Portal: which sections the spa shows + paying into the balance ─────
  portalSections: { packages: boolean; memberships: boolean; accountBalance: boolean } =
    { packages: true, memberships: true, accountBalance: true };
  /** The spa lets clients pay into their account balance online. */
  balancePaymentsEnabled = false;
  walletPayAmount = '';
  /** A card on file's id, 'new' for the card field, null = not chosen yet. */
  walletPayCardId: number | 'new' | null = null;
  walletPaySaveCard = true;
  walletPaying = false;
  walletPayError: string | null = null;
  walletPaySuccess: { amount: number; saleNumber: string; accountBalance: number; cardBrand: string; cardLast4: string } | null = null;
  /** One id per attempt: a double tap on Pay settles ONE charge. */
  walletPayAttemptId = '';
  /** A card is being made default / removed / added (buttons disabled). */
  walletCardBusy = false;
  walletCardError: string | null = null;
  /** The card whose removal is being confirmed (the Wallet's own modal). */
  walletRemoveCardId: number | null = null;
  /** The Wallet's own "Add a card" form is open. */
  walletAddCardOpen = false;
  // ── Portal as an app (PWA) ────────────────────────────────────────────
  /** Chrome's deferred install prompt (beforeinstallprompt), when offered. */
  installPrompt: { prompt: () => Promise<unknown>; userChoice?: Promise<{ outcome: string }> } | null = null;
  /** Running as the installed app (standalone display mode). */
  runningAsApp = false;
  installDismissed = false;
  installDone = false;
  /** iPhone/iPad Safari: no prompt — the Share → Add to Home Screen steps instead. */
  isIosBrowser = false;
  installHelpOpen = false;

  /** Offer 'Add to your home screen' on the home screen? */
  get installOffer(): 'prompt' | 'ios' | null {
    if (!this.isStandalone || this.entryMode !== 'portal' || !this.clientId) return null;
    if (this.runningAsApp || this.installDismissed || this.installDone) return null;
    if (this.installPrompt) return 'prompt';
    if (this.isIosBrowser) return 'ios';
    return null;
  }

  /** Same-day openings right now (from /quick-book/today/), for the home card. */
  /** Open Today's state for the home card: `open` = something is free right now.
   *  null while Open Today is off (no card at all). */
  sameDayToday: { open: boolean } | null = null;

  /** A booking of theirs still waiting on its deposit, with a live link. */
  get pendingDepositBooking(): Booking | null {
    const now = Date.now();
    const live = (this.portalBookings || []).filter(b =>
      b.status === 'pending_deposit' && b.deposit && b.deposit.payExpiresAt
      && new Date(String(b.deposit.payExpiresAt)).getTime() > now);
    live.sort((a, b) => String(a.deposit!.payExpiresAt).localeCompare(String(b.deposit!.payExpiresAt)));
    return live[0] || null;
  }

  // ── Portal: my notes & preferences ───────────────────────────────────
  prefsDraft = '';
  prefsSaving = false;
  prefsSaved = false;
  prefsError: string | null = null;
  /** Tags the client switches themselves (portal-managed), grouped as the spa labelled them. */
  portalTagGroups: import('./api').PortalTagGroup[] = [];
  portalTagsBusy = false;
  portalTagsError: string | null = null;
  /** 'Leave us a review' (Tim, 2026-11-02): the spa's review page, or null while off. */
  reviewPrompt: { host: string; url: string; remindDays: number; message?: string } | null = null;
  reviewPromptBusy = false;
  /** The banner shows while the spa asks, the client has not said 'done', and no snooze is running. */
  get reviewPromptDue(): boolean {
    const c = this.client;
    if (!this.reviewPrompt || !c) return false;
    if (c.reviewPromptDoneAt) return false;
    const until = c.reviewPromptSnoozedUntil ? Date.parse(c.reviewPromptSnoozedUntil) : 0;
    return !(until && until > Date.now());
  }
  /** Push notifications on THIS device (the installed portal; Tim, 2026-11-02). */
  pushSupported = false;
  pushEnabled = false;
  pushDevices = 0;
  pushBusy = false;
  pushError: string | null = null;
  pushNote: string | null = null;
  pushLoaded = false;
  /** 'My details' on the preferences screen (Tim, 2026-11-02). */
  detailsDraft: Record<string, string> = {};
  detailsSaving = false;
  detailsSaved = false;
  detailsError: string | null = null;
  marketingPrefsSaving = false;
  marketingPrefsError: string | null = null;

  /** The portal confirm step's 'you agree to our cancellation policy' line, opened. */
  policyLineOpen = false;

  // ── Portal: 'Send us a message' ───────────────────────────────────────
  messageText = '';
  messageSending = false;
  messageSent = false;
  messageError: string | null = null;
  /** 'Email me this receipt' on the sale detail: idle | sending | sent | error. */
  receiptEmailState: 'idle' | 'sending' | 'sent' | 'error' = 'idle';
  receiptEmailNote = '';
  /** A form being opened / started from the home screen (buttons disabled). */
  portalFormBusy = false;

  /** Today on the device's own clock as YYYY-MM-DD — never toISOString(),
   *  which east of UTC is yesterday after a local midnight. */
  get todayStr(): string {
    return this.today.toLocaleDateString('en-CA');
  }

  /** The installed app can sit open for days: 'today' follows the clock. */
  refreshToday(): void {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    this.today = now;
  }

  /** The next visit on the books (soonest upcoming, not cancelled/done). */
  get nextAppointment(): Booking | null {
    const today = this.todayStr;
    const live = (this.portalBookings || []).filter(b => b.date >= today && !PortalState.NOT_UPCOMING.includes(b.status));
    live.sort((a, b) => (a.date + (a.items[0]?.startTime || '')).localeCompare(b.date + (b.items[0]?.startTime || '')));
    return live[0] || null;
  }

  /** 'Your usual': the service + provider they have booked most often (past
   *  visits that happened), as the most recent such booking — the rebook
   *  path takes a booking. Null until two visits agree. */
  get usualBooking(): Booking | null {
    const today = this.todayStr;
    const past = (this.portalBookings || []).filter(b =>
      b.items?.length && b.date < today && !['cancelled', 'expired', 'noshow', 'no-show', 'waitlist', 'pending_deposit'].includes(b.status));
    const counts = new Map<string, { n: number; latest: Booking }>();
    for (const b of past) {
      const it = b.items[0];
      const key = `${it.serviceName}|${it.staffName}`;
      const cur = counts.get(key);
      if (!cur) counts.set(key, { n: 1, latest: b });
      else { cur.n += 1; if (b.date > cur.latest.date) cur.latest = b; }
    }
    let best: { n: number; latest: Booking } | null = null;
    for (const v of counts.values()) if (!best || v.n > best.n) best = v;
    return best && best.n >= 2 ? best.latest : null;
  }

  /** Forms waiting on the client for a visit still ahead — attached to an
   *  upcoming booking, or unattached and recent. An old pending form from a
   *  visit long past is not 'before your visit' (38 of them showed, 2026-10-13). */
  get formsToComplete(): FormSubmission[] {
    const today = this.todayStr;
    const upcoming = new Set((this.portalBookings || [])
      .filter(b => b.date >= today && !PortalState.NOT_UPCOMING.includes(b.status)).map(b => b.id));
    const recent = new Date(this.today.getTime() - 30 * 86400000).toLocaleDateString('en-CA');
    return (this.formSubmissions || []).filter(f =>
      (f.whoFills || 'client') === 'client'
      && ['pending', 'awaiting_client', 'draft', 'sent'].includes(f.status)
      && (f.appointmentId ? upcoming.has(Number(f.appointmentId)) : (f.createdAt || '').slice(0, 10) >= recent));
  }

  /** The forms still wanted for ONE appointment — shown on its details too (Tim, 2026-10-18). */
  formsForBooking(bookingId: number): FormSubmission[] {
    return this.formsToComplete.filter(f => Number(f.appointmentId) === Number(bookingId));
  }

  /** Completed intake forms older than a year whose template the spa still
   *  uses, one per template, with no newer copy already on file. */
  get staleForms(): FormSubmission[] {
    const all = this.formSubmissions || [];
    const cutoff = new Date(this.today.getTime() - 365 * 86400000).toLocaleDateString('en-CA');
    const newestByTemplate = new Map<number, FormSubmission>();
    for (const f of all) {
      const when = (f.submittedAt || f.createdAt || '').split('T')[0];
      const cur = newestByTemplate.get(f.templateId);
      const curWhen = cur ? (cur.submittedAt || cur.createdAt || '').split('T')[0] : '';
      if (!cur || when > curWhen) newestByTemplate.set(f.templateId, f);
    }
    return [...newestByTemplate.values()].filter(f =>
      f.templateActive !== false && (f.whoFills || 'client') === 'client'
      && ['completed', 'approved'].includes(f.status)
      && (f.submittedAt || f.createdAt || '').split('T')[0] < cutoff);
  }

  /** "Book for me & someone else" from the portal: the PUBLIC flow (groups,
   *  several services, guest details) run for the signed-in client; when it
   *  ends, back to the dashboard as the portal. */
  portalGroupBooking = false;

  /** What the Wallet's pay screen will charge, or 0 when the amount is not a
   *  valid sum yet. */
  get walletPayAmountNum(): number {
    const n = Math.round(parseFloat(String(this.walletPayAmount).replace(/[^0-9.]/g, '')) * 100) / 100;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /** The balance owed (a negative balance), as a positive figure, else 0. */
  get walletOwed(): number {
    const b = this.wallet?.accountBalance ?? this.client?.accountBalance ?? 0;
    return b < 0 ? Math.round(-b * 100) / 100 : 0;
  }

  openWalletPay(): void {
    this.walletPayAmount = this.walletOwed > 0 ? this.walletOwed.toFixed(2) : '';
    const cards = (this.wallet?.creditCards || []).filter(c => c.hasSavedCard !== false);
    const def = cards.find(c => c.isDefault) || cards[0];
    this.walletPayCardId = def ? def.id : 'new';
    this.walletPaySaveCard = true;
    this.walletPaying = false;
    this.walletPayError = null;
    this.walletPaySuccess = null;
    this.walletPayAttemptId = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    this.navigate('wallet-pay');
  }
  packagesLoading = false;
  membershipsLoading = false;
  saleDetailLoading = false;

  // ── Booking flow API data ─────────────────────────────────────────────
  categories: Category[] = [];
  enhGroups: Record<string, EnhancementGroup> = {};
  providers: Provider[] = [];
  formConfig: FormConfig = { fields: [], notices: [] };
  availabilityConfig: AvailabilityConfig = { intervalMinutes: 30, sameDayMessage: 'Same-day booking not available online.' };
  /** /book/ (auto): the regular flow was swapped in from the same-day surface — offer a way back */
  canReturnToToday = false;
  gcConfig: GiftCardConfig = { artworkUrl: null, minAmount: 25, maxAmount: 500, disclaimer: '', promoDisclaimer: '', requireTermsAgreement: false, successMessage: '', viewCardBaseUrl: '' };
  gcAgreedTerms = false;
  gcPromotions: GiftCardPromotion[] = [];
  bookingDataLoaded = false;
  bookingDataLoading = false;
  bookingDataLoadedAt = 0;  // timestamp (ms) when data was last fetched

  // ── Location selection ───────────────────────────────────────────────
  bookingLocations: BookingLocation[] = [];
  selectedLocationId: number | null = null;

  // ── Online booking settings (from widget-config) ────────────────────
  allowGroupBookings = false;
  maxGroupSize = 4;
  groupBookingLocationIds: number[] = [];        // [] = all locations allowed
  groupBookingExcludedServiceIds: number[] = []; // services excluded from group booking

  // ── Cancel / reschedule settings ───────────────────────────────────
  selfCancelHours = 0;
  selfRescheduleHours = 0;
  cancelLoading = false;
  /** Confirm / I'm here in flight on the portal's appointment details. */
  portalActionBusy = false;
  rescheduleLoading = false;
  rescheduleBookingId: number | null = null;
  /** Reschedule (Tim, 2026-11-02): the therapists the client ASKED for on
   *  this visit (their times only, unless they opt into others), and whether
   *  they opened the grid to any therapist. */
  rescheduleRequestedNames: string[] = [];
  rescheduleAnyProvider = false;
  /** The reschedule in progress is a REQUEST (staff approve it), not a move. */
  rescheduleAsRequest = false;
  /** One-line notice on the bookings list after a request is sent. */
  portalNotice: string | null = null;

  // ── Online booking settings ──────────────────────────────────────────
  allowMultipleServices = true;
  allowSameServiceMultiple = false;
  showBookForSomeoneElse = true;
  onlyAllowSingleStaff = false;
  portalSessionDays = 0;  // 0 = 24h default

  // ── "Nothing available — get back to me" form (date step) ───────────
  callbackName = '';
  callbackPhone = '';
  callbackEmail = '';        // optional — how else to reach them, and what "Create client" reads
  callbackMessage = '';
  callbackPreference: 'either' | 'text' | 'call' = 'either';
  callbackStatus: 'idle' | 'sending' | 'sent' | 'error' = 'idle';
  /** set by "Not you?" on the callback form: type the details instead */
  callbackTypeDetails = false;

  /**
   * Who the callback form already knows: the signed-in client, else whoever
   * this device remembers. Then it asks for a message, not for details they
   * have given us before (and "Not you?" hands the fields back).
   */
  callbackKnownContact(): { name: string; phone: string } | null {
    if (this.callbackTypeDetails) return null;
    if (this.client?.phone) {
      const name = this.client.name
        || [this.client.firstName, this.client.lastName].filter(Boolean).join(' ');
      if (name) return { name, phone: this.client.phone };
    }
    if (!this.rememberOnDevice || this.entryMode === 'portal') return null;
    const r = loadRemembered(this.apiBase);
    const name = r ? [r.firstName, r.lastName].filter(Boolean).join(' ') : '';
    return r && r.phone && name ? { name, phone: r.phone } : null;
  }

  // ── Waitlist ────────────────────────────────────────────────────────
  waitlistMode = false;
  waitlistTimeBlock: string = '';
  allowWaitlist = false;
  showWaitlistPicker = false;

  // ── Current booking item being built ──────────────────────────────────
  curr: CurrentItem = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
  expandedCat: string | null = null;
  modalSvc: Service | null = null;

  // ── Multi-service cart ────────────────────────────────────────────────
  items: BookingItem[] = [];

  // ── Calendar state ────────────────────────────────────────────────────
  today: Date;
  weekOffset = 0;
  selectedDate: string | null = null;
  selectedTime: string | null = null;
  expandedPeriod: string | null = null;
  // Real availability from API (date → Set of "H:MM am/pm" strings per provider)
  _availableSlots: Map<string, Set<string>> = new Map();
  /** same-day promo per availability cache key (only when that fetch used the Open Today engine) */
  _sameDayPromo: Map<string, SameDayPromo | null> = new Map();
  /** quiet-hours discount per slot time, per availability cache key: {'9:00 am': {pct, price}} */
  _quietHours: Map<string, { label: string; price: number; byTime: Record<string, { pct: number; price: number }> }> = new Map();
  _slotsLoading = false;
  /** Availability requests that FAILED (cache key): the date step says so and
   *  offers a retry instead of 'no times available'. */
  _slotsFailed = new Set<string>();
  _onChangeCallback: (() => void) | null = null;

  // ── Contact form state ────────────────────────────────────────────────
  formData: Record<string, unknown> = {};
  agreedToPolicy = false;
  // Tapping Continue while it is blocked turns this on: every missing thing is
  // listed above the button and ringed in the form. Cleared on step change and
  // hidden automatically the moment canProceed turns true.
  showBlockedSummary = false;
  // Deposit-step policy agreement (portal payers — no contact-step toggle).
  depositPolicyAgreed = false;
  cancellationPolicyHtml = '';

  // ── Book for someone else ─────────────────────────────────────────────
  bookForOther = false;
  otherFirstName = '';
  otherLastName = '';
  otherEmail = '';
  otherPhone = '';
  responsibleAgreed = false;
  /** Book for someone else (portal): 'This person is under 18' (Tim, 2026-11-03). */
  otherIsMinor = false;

  // ── Group booking ──────────────────────────────────────────────────────
  groupSize = 1;
  currentGuestIndex = 0;
  guests: GuestInfo[] = [];

  // ── Promo code state ──────────────────────────────────────────────────
  promoStatus: 'idle' | 'loading' | 'valid' | 'invalid' = 'idle';
  promoOffer: { offerId: number; offerName: string; discountAmount: number; discountLabel: string } | null = null;
  promoError: string = '';

  // ── Stripe ──────────────────────────────────────────────────────────
  stripePublishableKey: string = '';
  stripeAccountId: string = '';  // Stripe Connect acct_… (pairs with platform pk)
  stripePaymentMethodId: string = '';
  depositCollectionMethod: string = 'payment_link';  // 'payment_link' | 'venmo'

  // ── Returning-client phone lookup (contact step) ──
  // Anonymous: the server only says "this number books here" (+ whether a
  // card is on file, so the deposit step can be skipped honestly). Known →
  // the rest of the contact form collapses; we already have their details.
  knownClient: { phone: string; cardOnFile: boolean } | null = null;
  phoneLookupBusy: boolean = false;
  /** The number they typed holds a membership that does NOT cover the provider
   *  they picked — said here, three steps before the card field. */
  membershipProviderMismatch = false;
  /** "No thanks" on the post-booking offer — asked once, not on every render. */
  upsellDismissed = false;

  // ── In-widget deposit step (after submit, before "You're all set") ──
  deposit: DepositLink | null = null;
  depositPaying: boolean = false;
  depositPayError: string | null = null;
  // Shop-cart pickup: pay for the products now (reserved) or at pickup (not).
  retailPrepayEnabled = false;
  retailPickupServiceId: number | null = null;
  productsPayNow = true;           // pre-selected — paying ahead is what we encourage
  productsPrepaid = false;         // the products were paid online (success copy)
  depositPaidSplit: { giftCard: number; card: number; tip: number } | null = null;  // how the deposit was settled, for the success copy
  depositTextState: 'idle' | 'sending' | 'sent' | 'failed' = 'idle';
  depositPhoneLast4: string = '';
  /** "Leaving the deposit?" — the close gate once the booking is real. */
  leaveDepositModal: boolean = false;
  leaveDepositBusy: boolean = false;
  /** A saved draft found at open time; the resume screen offers it. */
  resumeDraft: import('./draft').BookingDraft | null = null;
  resumeChecking: boolean = false;
  /** What the freshness check found, shown on the step we land on. */
  resumeNotice: string | null = null;
  /** 409 hold_exists from POST /bookings/: the live deposit hold this person
   *  already has (server utils/live_hold). The screen asks pay / release / keep. */
  existingHold: ExistingHold | null = null;
  existingHoldBusy = false;
  /** The answer to ride on the re-POST ('release' | 'keep'), then cleared. */
  existingHoldChoice: 'release' | 'keep' | null = null;
  /** The step whose submit was refused — where an answer returns to. */
  existingHoldFrom: Step | null = null;

  // Custom message on the deposit screen (the same depositPageNotice the
  // texted payment page shows), and the gift-card capture: number field +
  // agreement toggle when enabled. The number is NEVER validated in the
  // browser — the server resolves it privately at charge time.
  depositPageNotice: string = '';
  depositGiftCard: { enabled: boolean; agreement: string } = { enabled: false, agreement: '' };
  depositGcOpen: boolean = false;
  depositGcNumber: string = '';
  depositGcAgreed: boolean = false;

  // ── Gift card state ───────────────────────────────────────────────────
  gcSelectedPromotion: GiftCardPromotion | null = null;
  gcPurchaseFor: 'someone' | 'yourself' = 'someone';
  gcAmount: number | string = '';
  gcYourDetails = { firstName: '', lastName: '', email: '', phone: '' };
  gcRecipient = { firstName: '', lastName: '', message: '', email: '', phone: '' };
  gcPaymentData = { fullName: '', country: 'US', address: '', cardNumber: '', expiry: '', cvc: '' };
  gcSubmitting = false;
  gcPayError: string | null = null;
  gcCardViewUrl: string | null = null;
  // Id of the "pending purchase" lead recorded on Continue; echoed back on the
  // final purchase so the server can mark it completed.
  gcPendingId: number | null = null;

  // ── Birthday prompt (post-booking) ──────────────────────────────────
  promptBirthdayAfterBooking = false;
  lastBookingId: number | null = null;
  lastBookingConfCode = '';
  lastBookingClientId: number | null = null;
  birthdayPromptValue = '';
  birthdayPromptSaving = false;
  birthdayPromptSaved = false;

  // ── Booking submission state ──────────────────────────────────────────
  bookingSubmitting = false;
  bookingError: string | null = null;
  // The queue had not answered when the poll window closed. The booking will
  // almost certainly complete — but "You're all set" is a claim, and until the
  // outcome arrives it would be a guess. The success screen softens instead.
  bookingOutcomePending = false;

  // ── Package state ─────────────────────────────────────────────────────
  packages: PackageTemplate[] = [];
  selectedPackage: PackageTemplate | null = null;
  pkgContact = { firstName: '', lastName: '', phone: '', email: '' };
  pkgPaymentData = { cardNumber: '', expiry: '', cvc: '' };
  pkgSubmitting = false;
  pkgPayError: string | null = null;

  // ── Membership enrollment state ─────────────────────────────────────
  membershipPlans: MembershipPlanPublic[] = [];
  /** "Only what my membership covers" — off by default, even for a member: on
   *  by default hides services they might happily pay for, and loses the sale. */
  membershipFilterPlanId: number | null = null;

  /**
   * What the booking flow needs to label anything: the plans on sale, and the
   * ones this client actually holds.
   *
   * Owned plans come from a live portal session — the only identity the widget
   * can prove. A device-remembered client has an id but no authority to read
   * their memberships, so they are advertised to like anyone else and caught by
   * the phone-lookup backstop at the contact step.
   */
  get eligibility(): { plans: MembershipPlanPublic[]; ownedPlanIds: number[] } {
    const REDEEMABLE = ['active', 'cancelling'];
    const ownedPlanIds = (this.memberships || [])
      .filter(m => REDEEMABLE.includes(m.status))
      .map(m => Number(m.planId));
    return { plans: this.membershipPlans, ownedPlanIds };
  }

  /** True when the widget should say nothing about memberships at all. */
  get membershipsSilent(): boolean {
    return this.membershipPlans.length === 0 && this.eligibility.ownedPlanIds.length === 0;
  }

  /** The plan the filter is pinned to, if any. */
  get filterPlan(): MembershipPlanPublic | null {
    if (this.membershipFilterPlanId == null) return null;
    return this.membershipPlans.find(p => Number(p.id) === this.membershipFilterPlanId) || null;
  }
  selectedMembershipPlan: MembershipPlanPublic | null = null;
  memContact = { firstName: '', lastName: '', phone: '', email: '' };
  memPaymentData = { cardNumber: '', expiry: '', cvc: '' };
  memSubmitting = false;
  memPayError: string | null = null;

  // ── Portal one-click purchase/enrollment state ──────────────────────
  portalPurchaseStep: 'confirm' | 'add-card' | 'processing' | null = null;
  portalPurchaseType: 'package' | 'membership' | null = null;
  portalPurchaseTemplateId: number | null = null;
  portalPurchaseError: string | null = null;
  portalError: string | null = null;
  portalPurchaseAgreed = false;
  portalPurchaseSuccess = false;

  constructor(cfg: WidgetConfig) {
    this.spaName = cfg.spaName || 'Client Portal';
    this.spaLogoUrl = cfg.logoUrl || null;
    // "Standalone" = the booking panel IS the page (hosted /book/, /portal/).
    // The hosted /shop/ page is standalone too, but the catalog is the page
    // and the panel is a closable overlay on top of it — otherwise it renders
    // with no close button and closePanel() is a no-op (the 2026-08 shop bug).
    // Same for the hosted /menu/ price list.
    this.isStandalone = cfg.mode === 'standalone' && !cfg.catalog && !cfg.priceList;
    this.apiBase = cfg.apiBase || '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.today = today;
  }

  // ── Chat session persistence ─────────────────────────────────────────

  private static _CHAT_KEY = 'spa_chat_session';

  saveChatSession(): void {
    try {
      const data = {
        conversationId: this.aiConversationId,
        sessionKey: this.aiSessionKey,
        lastMsgId: this.aiLastMsgId,
        messages: this.aiMessages.map(m => ({
          role: (m as any).role,
          text: m.text,
          options: m.options || undefined,
          quickReplies: (m as any).quickReplies || undefined,
        })),
        phoneSubmitted: (this as any).aiPhoneSubmitted || false,
      };
      localStorage.setItem(PortalState._CHAT_KEY, JSON.stringify(data));
    } catch { /* quota exceeded or private browsing */ }
  }

  restoreChatSession(): boolean {
    try {
      const raw = localStorage.getItem(PortalState._CHAT_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (!data.conversationId) return false;
      this.aiConversationId = data.conversationId;
      this.aiSessionKey = data.sessionKey || '';
      this.aiLastMsgId = data.lastMsgId || 0;
      this.aiMessages = (data.messages || []).map((m: any) => ({
        role: m.role,
        text: m.text,
        options: m.options,
        quickReplies: m.quickReplies,
      }));
      (this as any).aiPhoneSubmitted = data.phoneSubmitted || false;
      return true;
    } catch { return false; }
  }

  clearChatSession(): void {
    this.aiMessages = [];
    this.aiConversationId = null;
    this.aiSessionKey = '';
    this.aiLastMsgId = 0;
    this.aiPaused = false;
    (this as any).aiPhone = '';
    (this as any).aiPhoneCollapsed = false;
    (this as any).aiPhoneSubmitted = false;
    if (this._aiPollTimer) { clearInterval(this._aiPollTimer); this._aiPollTimer = null; }
    try { localStorage.removeItem(PortalState._CHAT_KEY); } catch {}
  }

  // ── Initialize from API data ──────────────────────────────────────────

  setBookingData(data: ApiData): void {
    this.categories = data.categories;
    this.enhGroups = data.enhancementGroups;
    this.providers = data.providers;
    this.formConfig = data.formConfig;
    this.availabilityConfig = data.availabilityConfig;
    this.allowWaitlist = !!(data.availabilityConfig as any)?.allowWaitlist;
    this.gcConfig = data.giftCardConfig;
    this.gcPromotions = data.gcPromotions || [];
    this.bookingLocations = data.bookingLocations || [];
    this.bookingDataLoaded = true;
    // open({serviceId}) arrived before the menu: honour it now (no-op when the
    // service isn't online-bookable — the client just sees the menu).
    if (this.pendingServiceId && !this.needsLocationSelection) {
      const sid = this.pendingServiceId;
      this.pendingServiceId = null;
      this.pickService(sid);
    }
    this.bookingDataLoading = false;
    this.bookingDataLoadedAt = Date.now();

    // Pre-populate form data with defaultValues
    this.formData = Object.fromEntries(
      data.formConfig.fields
        .filter(f => f.defaultValue !== undefined)
        .map(f => [f.id, f.defaultValue])
    );
  }

  // ── Navigation ────────────────────────────────────────────────────────

  /**
   * The membership dimension carried on the events that already fire.
   *
   * Cheaper and truer than an event per pill rendered: those would flood the
   * table for a number that falls out of what was SHOWN vs what was CHOSEN.
   */
  memDim(extra: Record<string, unknown> = {}): Record<string, unknown> {
    const e = this.eligibility;
    return {
      ...extra,
      memberOf: e.ownedPlanIds,
      memFilter: this.membershipFilterPlanId,
    };
  }

  /**
   * How many options on this step a membership covered — the "shown" half of
   * shown-vs-selected. Zero on steps where the question doesn't arise.
   */
  eligibleShownOn(step: Step): number {
    const e = this.eligibility;
    if (!e.plans.length && !e.ownedPlanIds.length) return 0;
    try {
      if (step === 'book-categories') {
        let n = 0;
        for (const cat of this.categories) {
          for (const svc of cat.services) {
            if (serviceVerdict(e, svc.id).kind !== 'none') n++;
          }
        }
        return n;
      }
      if (step === 'book-provider' && this.curr.service) {
        return this.filteredProviders.filter(p =>
          !p.type?.startsWith('filter')
          && providerVerdict(e, this.curr.service!.id, p.id).kind !== 'none').length;
      }
      if (step === 'book-enhancements') {
        let n = 0;
        for (const grp of this.currEnhGroups) {
          for (const a of grp.addOns) {
            if (enhancementVerdict(e, a.id).kind !== 'none') n++;
          }
        }
        return n;
      }
    } catch { /* analytics must never break a booking */ }
    return 0;
  }

  /** Dead ends already reported, so a re-render doesn't double-count. */
  private _deadEnds = new Set<string>();

  /** The post-booking offer was put in front of someone. Once per plan. */
  noteUpsellShown(planId: number): void {
    const key = `upsell:${planId}`;
    if (this._deadEnds.has(key)) return;
    this._deadEnds.add(key);
    trackWidgetEvent('membership_upsell_shown', this.memDim({ planId }));
  }

  /** A membership filter left this step with nothing bookable. */
  noteFilterDeadEnd(planId: number): void {
    const key = `${planId}:${this.step}`;
    if (this._deadEnds.has(key)) return;
    this._deadEnds.add(key);
    trackWidgetEvent('membership_filter_empty', { planId, step: this.step });
  }

  navigate(step: Step): void {
    this.stepHistory.push(this.step);
    this.step = step;
    // The add-another follow-up question is transient — any navigation
    // (including Back re-entering the step) starts from the plain Yes/No.
    this.addAnotherWho = false;
    // The blocked-Continue banner belongs to the step that raised it.
    this.showBlockedSummary = false;
    trackWidgetEvent('step_enter', this.memDim({
      step, itemCount: this.items.length, eligibleShown: this.eligibleShownOn(step),
    }));
    if (step === 'book-contact') this.prefillContactFromDevice();
  }

  /**
   * Enter a step WITHOUT pushing history (for programmatic transitions such as
   * "no more services → datetime" or "time picked → contact"), but still emit
   * `step_enter` so the funnel report sees every step. Before this existed those
   * transitions assigned `state.step` directly and the date/contact steps were
   * invisible in analytics.
   */
  setStep(step: Step): void {
    this.step = step;
    trackWidgetEvent('step_enter', this.memDim({
      step, itemCount: this.items.length, eligibleShown: this.eligibleShownOn(step),
    }));
    if (step === 'book-contact') this.prefillContactFromDevice();
  }

  // ── Remembered on this device (no lookup, no code) ────────────────────

  /** Prefill empty contact fields from the device record; leaves anything the
   *  person already typed alone. Public flow only (portal has the profile). */
  prefillContactFromDevice(): void {
    // Signed in (the portal's "book for me & someone else" runs the public
    // flow): the booker's details come from their record, not the device.
    if (this.clientId && this.client) {
      const c = this.client;
      const set = (k: string, v: unknown) => { if (v && !this.formData[k]) this.formData[k] = v; };
      set('firstName', c.firstName); set('lastName', c.lastName);
      set('phone', c.phone); set('email', c.email);
      return;
    }
    if (!this.rememberOnDevice || this.entryMode === 'portal' || this.clientId) return;
    const r = loadRemembered(this.apiBase);
    if (!r) { this.rememberedFirstName = ''; return; }
    const set = (k: string, v: unknown) => { if (v && !this.formData[k]) this.formData[k] = v; };
    set('firstName', r.firstName); set('lastName', r.lastName);
    set('phone', r.phone); set('email', r.email);
    if (r.smsOpt && !this.formData.smsOpt) this.formData.smsOpt = r.smsOpt;
    this.rememberedFirstName = r.firstName || '';
    trackWidgetEvent('device_prefill', { entryMode: this.entryMode });
  }

  /** "Not you?" — clear the device record and the prefilled fields. */
  forgetDevice(): void {
    forgetRemembered(this.apiBase);
    // The recognition token goes with it: "not me" has to mean the device stops
    // speaking for that person at all, not just stops prefilling.
    forgetRecognition(this.apiBase);
    this.memberships = null;
    for (const k of ['firstName', 'lastName', 'phone', 'email', 'smsOpt']) delete this.formData[k];
    this.rememberedFirstName = '';
  }

  /** After a successful public booking: remember for next time. */
  rememberContactOnDevice(clientId?: number | null): void {
    // A signed-in client (the portal, or its group booking) is never written
    // into the device's memory — a shared phone would prefill them for everyone.
    if (!this.rememberOnDevice || this.entryMode === 'portal' || this.clientId || this.portalGroupBooking) return;
    const f = this.formData;
    saveRemembered(this.apiBase, {
      firstName: String(f.firstName || ''), lastName: String(f.lastName || ''),
      phone: String(f.phone || ''), email: String(f.email || ''),
      smsOpt: (f.smsOpt === 'yes' || f.smsOpt === 'no') ? f.smsOpt : '',
      clientId: clientId ?? undefined,
    });
  }

  /** Days between the clinic-local "today" and a YYYY-MM-DD date (0 = today). */
  dateOffsetDays(dateStr: string | null): number | null {
    if (!dateStr) return null;
    const today = this.today.toISOString().split('T')[0];
    const a = new Date(dateStr + 'T00:00:00Z').getTime();
    const b = new Date(today + 'T00:00:00Z').getTime();
    return Math.round((a - b) / 86400000);
  }

  goBack(): void {
    // For booking flow steps, use deterministic back navigation
    const bookingBackMap: Partial<Record<Step, Step>> = {
      'book-ai':           this.needsLocationSelection ? 'book-location' : 'book-categories',
      'book-categories':   this.needsLocationSelection ? 'book-location' : undefined,
      'book-enhancements': 'book-categories',
      'book-provider':     this.curr.service?.enhancementGroupIds?.length ? 'book-enhancements' : 'book-categories',
      'book-addAnother':   'book-provider',
      'book-datetime':     this.items.length > 0 ? 'book-addAnother' : 'book-provider',
      'book-contact':      'book-datetime',
      'gc-payment':        'gc-details',
      'pkg-contact':       'pkg-list',
      'pkg-payment':       'pkg-contact',
      'mem-contact':       'mem-list',
      'mem-payment':       'mem-contact',
    };

    const mapped = bookingBackMap[this.step];
    if (mapped) {
      // Going back from addAnother/datetime to provider: restore the last item into curr
      if ((this.step === 'book-addAnother' || this.step === 'book-datetime') && mapped === 'book-provider' && this.items.length > 0) {
        this.curr = this.items.pop()!;
      }
      this.step = mapped;
      // Also pop from history to stay in sync
      this.stepHistory.pop();
      return;
    }

    // For gc-details: go back to categories if coming from booking, otherwise use history
    if (this.step === 'gc-details' && this.entryMode !== 'giftcard') {
      this.step = 'book-categories';
      this.stepHistory.pop();
      return;
    }

    // For pkg-list / mem-list: go back to categories
    if (this.step === 'pkg-list' || this.step === 'mem-list') {
      this.step = 'book-categories';
      this.stepHistory.pop();
      return;
    }

    // For portal steps, use history — but never go back to login/otp when logged in
    const prev = this.stepHistory.pop();
    if (prev) {
      if (this.clientId && (prev === 'login' || prev === 'otp')) {
        this.step = 'dashboard';
        this.stepHistory = [];
      } else {
        this.step = prev;
      }
    }
  }

  get canGoBack(): boolean {
    // Always allow back from booking/gc/pkg steps (except first step of entry)
    if (this.step === 'book-location' && this.entryMode === 'booking' && !this.clientId) return false;
    if (this.step === 'book-categories' && this.entryMode === 'booking' && !this.clientId && !this.needsLocationSelection) return false;
    if (this.step === 'gc-details' && this.entryMode === 'giftcard') return false;
    // The booking is already written once we reach the deposit screens — there
    // is nothing to go "back" to; the way out is paying, texting, or closing.
    if (this.step === 'book-payment' || this.step === 'book-pending-text' || this.step === 'book-resume' || this.step === 'book-hold-exists' || this.step === 'book-card-on-file') return false;
    if (this.step.startsWith('book-') || this.step.startsWith('gc-') || this.step.startsWith('pkg-') || this.step.startsWith('mem-')) return true;
    // Don't show back from dashboard when logged in (nowhere useful to go back to)
    if (this.step === 'dashboard' && this.clientId) return false;
    return this.stepHistory.length > 0;
  }

  // ── Panel ─────────────────────────────────────────────────────────────

  openPanel(): void {
    // Refresh "today" each time the panel opens — prevents stale date
    // when the host page is left open overnight
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    this.today = now;

    // Invalidate cached booking data if older than 5 minutes so
    // providers, schedules, services, etc. are re-fetched fresh
    if (this.bookingDataLoaded && Date.now() - this.bookingDataLoadedAt > 30 * 60 * 1000) {
      this.bookingDataLoaded = false;
    }

    this.panelOpen = true;
    if (!this.isStandalone) lockPage();
  }

  closePanel(): void {
    if (this.isStandalone) return;
    this.panelOpen = false;
    unlockPage();
  }

  // ── Login ─────────────────────────────────────────────────────────────

  /** The home screen's FIRST load holds on one loading screen until every
   *  piece is in, then paints at once (Tim, 2026-11-03 — the review banner,
   *  Open Today and the referral link popped in seconds after the rest).
   *  `dashboardLoaded` = this person's home has painted once; later refreshes
   *  (back from a booking, a new day) keep the screen and fill in quietly. */
  dashboardHolding = false;
  dashboardLoaded = false;

  loginSuccess(client: ClientProfile, token?: string): void {
    if (this.clientId !== client.id) this.dashboardLoaded = false;
    // Sign-in lands on the home: hold it until its pieces are in (the widget's
    // prefetchDashboardData releases it; a widget with no API never holds).
    if (!this.dashboardLoaded && this.apiBase) this.dashboardHolding = true;
    this.clientId = client.id;
    this.client = client;
    this.clientToken = token || null;
    this.loginError = null;
    this.loginResults = null;
    this.multiAccountClients = null;
    // Clear login steps from history so back doesn't go to login/otp
    this.stepHistory = [];
    this.step = 'dashboard';
  }

  signOut(): void {
    clearClientSession();
    // A group booking in progress ends with the session (entryMode back to
    // the portal), and nothing of this person stays for the next one on a
    // shared phone.
    if (this.portalGroupBooking) { this.portalGroupBooking = false; this.entryMode = 'portal'; }
    this.resetBookingFlow();
    this.walletAddCardOpen = false; this.walletCardError = null; this.walletCardBusy = false; this.walletRemoveCardId = null;
    this.walletPayAmount = ''; this.walletPayCardId = null; this.walletPayError = null; this.walletPaySuccess = null; this.walletPaying = false;
    this.policyLineOpen = false;
    this.prefsDraft = ''; this.prefsSaved = false; this.prefsError = null; this.prefsSaving = false;
    this.detailsDraft = {}; this.detailsSaved = false; this.detailsError = null; this.detailsSaving = false;
    this.messageText = ''; this.messageSent = false; this.messageError = null; this.messageSending = false;
    this.receiptEmailState = 'idle'; this.receiptEmailNote = '';
    this.sameDayToday = null; this.installHelpOpen = false; this.portalFormBusy = false;
    this.dashboardHolding = false; this.dashboardLoaded = false;
    this.clientId = null;
    this.client = null;
    this.clientToken = null;
    this.portalBookings = null;
    this.sales = null;
    this.wallet = null;
    this.portalPackages = null;
    this.memberships = null;
    this.formSubmissions = null;
    this.selectedFormId = null;
    this.selectedFormDetail = null;
    this.recentBookings = [];
    this.rebookSource = null;
    this.selectedBookingId = null;
    this.selectedSaleId = null;
    this.portalPurchaseStep = null;
    this.portalPurchaseType = null;
    this.portalPurchaseTemplateId = null;
    this.portalPurchaseError = null;
    this.portalPurchaseAgreed = false;
    this.portalPurchaseSuccess = false;
    this.loginQuery = '';
    this.loginError = null;
    this.loginResults = null;
    this.otpSent = false;
    this.otpCode = '';
    this.otpDebugCode = null;
    this.otpNotice = null;
    this.multiAccountClients = null;
    this.multiAccountNote = null;
    this.stepHistory = [];
    this.step = 'login';
  }

  // ── Detail views ──────────────────────────────────────────────────────

  get selectedBooking(): Booking | null {
    if (this.selectedBookingId === null || !this.portalBookings) return null;
    return this.portalBookings.find(b => b.id === this.selectedBookingId) || null;
  }

  get selectedSale(): Sale | null {
    if (this.selectedSaleId === null || !this.sales) return null;
    return this.sales.find(s => s.id === this.selectedSaleId) || null;
  }

  // ── Dashboard stats ───────────────────────────────────────────────────

  /** Statuses that are NOT a visit still to come. One list for the pill,
   *  the next-visit card and the rest (see NOT_UPCOMING below). */
  static readonly NOT_UPCOMING = NOT_UPCOMING;

  get upcomingBookingsCount(): number {
    if (!this.portalBookings) return 0;
    const today = this.todayStr;
    return this.portalBookings.filter(b => b.date >= today && !PortalState.NOT_UPCOMING.includes(b.status)).length;
  }

  get activeMembershipsCount(): number {
    if (!this.memberships) return 0;
    return this.memberships.filter(m => m.status === 'active').length;
  }

  // ── Booking flow ──────────────────────────────────────────────────────

  startBooking(forOther = false): void {
    this.curr = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
    this.items = [];
    this.bookingCredit = null;
    this.cardOnFileBusy = false;
    this.cardOnFileError = null;
    this.selectedProducts = [];
    this.productPickerOpen = false;
    this.pendingServiceId = null;
    this.expandedCat = null;
    this.selectedDate = null;
    this.selectedTime = null;
    this.expandedPeriod = null;
    this.weekOffset = 0;
    this.bookingSubmitting = false;
    this.bookingError = null;
    this.bookingOutcomePending = false;
    this.showBlockedSummary = false;
    this.bookForOther = forOther;
    this.otherIsMinor = false;
    this.otherFirstName = '';
    this.otherLastName = '';
    this.otherEmail = '';
    this.otherPhone = '';
    this.responsibleAgreed = false;
    this.promoStatus = 'idle';
    this.promoOffer = null;
    this.promoError = '';
    this.groupSize = 1;
    this.currentGuestIndex = 0;
    this.guests = [];
    this.selectedLocationId = null;
    if (this.bookingDataLoaded) {
      this.formData = Object.fromEntries(
        this.formConfig.fields
          .filter(f => f.defaultValue !== undefined)
          .map(f => [f.id, f.defaultValue])
      );
    }
    // If multiple locations are enabled, show location picker first
    if (this.needsLocationSelection) {
      this.navigate('book-location');
    } else {
      // Auto-select if exactly one location
      if (this.bookingLocations.length === 1) {
        this.selectedLocationId = this.bookingLocations[0].id;
      }
      this.navigate('book-categories');
    }
  }

  get needsLocationSelection(): boolean {
    return this.bookingLocations.length > 1;
  }

  selectLocation(locationId: number): void {
    this.selectedLocationId = locationId;
    // A shop cart (open({serviceId})) waited for the location: go straight on.
    if (this.pendingServiceId) {
      const sid = this.pendingServiceId;
      this.pendingServiceId = null;
      this.pickService(sid);
      if (this.curr.service) return;
    }
    this.navigate('book-categories');
  }

  resetBookingFlow(): void {
    this.knownClient = null;
    this.phoneLookupBusy = false;
    this.deposit = null;
    this.depositPaying = false;
    this.depositPayError = null;
    this.depositTextState = 'idle';
    this.depositPhoneLast4 = '';
    this.items = [];
    this.curr = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
    this.expandedCat = null;
    this.selectedDate = null;
    this.selectedTime = null;
    this.selectedLocationId = null;
    this.expandedPeriod = null;
    this.agreedToPolicy = false;
    this.weekOffset = 0;
    this.bookingSubmitting = false;
    this.bookingError = null;
    this.bookingOutcomePending = false;
    this.showBlockedSummary = false;
    this.bookForOther = false;
    this.otherFirstName = '';
    this.otherLastName = '';
    this.otherEmail = '';
    this.otherPhone = '';
    this.responsibleAgreed = false;
    this.groupSize = 1;
    this.currentGuestIndex = 0;
    this.guests = [];
    this.promoStatus = 'idle';
    this.promoOffer = null;
    this.promoError = '';
    // Note: AI chat session persists across booking resets (stored in localStorage).
    // Only clear via clearChatSession() explicitly.
    this.aiLoading = false;
    this.aiInput = '';
    if (this._aiPollTimer) { clearInterval(this._aiPollTimer); this._aiPollTimer = null; }
    this.gcSelectedPromotion = null;
    this.gcPurchaseFor = 'someone';
    this.gcAmount = '';
    this.gcYourDetails = { firstName: '', lastName: '', email: '', phone: '' };
    this.gcRecipient = { firstName: '', lastName: '', message: '', email: '', phone: '' };
    this.gcAgreedTerms = false;
    this.gcPaymentData = { fullName: '', country: 'US', address: '', cardNumber: '', expiry: '', cvc: '' };
    this.gcShowMissing = false;
    this.gcSubmitting = false;
    this.gcPayError = null;
    this.gcCardViewUrl = null;
    this.gcPendingId = null;
    this.selectedPackage = null;
    this.pkgContact = { firstName: '', lastName: '', phone: '', email: '' };
    this.pkgPaymentData = { cardNumber: '', expiry: '', cvc: '' };
    this.pkgSubmitting = false;
    this.pkgPayError = null;
    this.selectedMembershipPlan = null;
    this.memContact = { firstName: '', lastName: '', phone: '', email: '' };
    this.memPaymentData = { cardNumber: '', expiry: '', cvc: '' };
    this.memSubmitting = false;
    this.memPayError = null;
    if (this.bookingDataLoaded) {
      this.formData = Object.fromEntries(
        this.formConfig.fields
          .filter(f => f.defaultValue !== undefined)
          .map(f => [f.id, f.defaultValue])
      );
    }
  }

  // ── Categories ────────────────────────────────────────────────────────

  toggleCat(id: string): void {
    this.expandedCat = this.expandedCat === id ? null : id;
  }

  openModal(svcId: string): void {
    for (const cat of this.categories) {
      const svc = cat.services.find(s => String(s.id) === String(svcId));
      if (svc) { this.modalSvc = svc; return; }
    }
  }

  pickService(svcId: string): void {
    for (const cat of this.categories) {
      const svc = cat.services.find(s => String(s.id) === String(svcId));
      if (svc) {
        this.curr = { service: svc, selectedAddons: [], provider: null, totalPrice: svc.price };
        // Booking with an add-on-only offer: the free add-on is ticked for them.
        if (this.creditAddonIds.length && this._creditServiceOk(svc) && this.creditItemIndex === -1) {
          for (const gid of svc.enhancementGroupIds || []) {
            const g = this.enhGroups[gid];
            const a = (g?.addOns || []).find(x => this.creditAddonIds.includes(String(x.id)));
            if (g && a) { this.toggleAddon(String(g.id), String(a.id)); break; }
          }
        }
        this.navigate(svc.enhancementGroupIds?.length ? 'book-enhancements' : 'book-provider');
        return;
      }
    }
  }

  // ── Enhancements ──────────────────────────────────────────────────────

  get currEnhGroups(): EnhancementGroup[] {
    if (!this.curr.service) return [];
    const groups = (this.curr.service.enhancementGroupIds || [])
      .map(id => this.enhGroups[id])
      .filter(Boolean);
    // A provider already chosen (a direct link, a rebook) hides the add-ons
    // they do not offer; in the usual order the provider step hides the
    // PROVIDERS a chosen add-on excludes instead (filteredProviders).
    const prov = this.curr.provider;
    if (!prov || prov.type?.startsWith('filter')) return groups;
    const pid = String(prov.id);
    return groups.map(g => ({
      ...g,
      addOns: (g.addOns || []).filter(a => !(a.excludedStaffIds || []).some(x => String(x) === pid)),
    }));
  }

  /** Required groups (with something to pick) the client has not chosen from.
   *  "Required" was a backoffice label only until 2026-09-19: Continue always
   *  went through, so a service whose PRICE depends on the choice was booked
   *  with no choice made. */
  get missingRequiredEnhGroups(): EnhancementGroup[] {
    return this.currEnhGroups.filter(g => g.required && (g.addOns || []).length
      && !g.addOns.some(a => this.isAddonOn(a.id)));
  }
  enhShowMissing = false;

  isAddonOn(id: string | number): boolean {
    return this.curr.selectedAddons.some(a => String(a.id) === String(id));
  }

  toggleAddon(grpId: string, addonId: string): void {
    const grp = this.enhGroups[grpId];
    if (!grp) return;
    const addon = grp.addOns.find(a => String(a.id) === String(addonId));
    if (!addon) return;

    const idx = this.curr.selectedAddons.findIndex(a => a.id === addon.id);
    if (idx >= 0) {
      this.curr.selectedAddons.splice(idx, 1);
    } else {
      if (grp.selectionRule === 'single') {
        this.curr.selectedAddons = this.curr.selectedAddons.filter(
          a => !grp.addOns.some(ga => ga.id === a.id)
        );
      }
      if (grp.maxSelections !== null) {
        const cnt = this.curr.selectedAddons.filter(a => grp.addOns.some(ga => ga.id === a.id)).length;
        if (cnt >= grp.maxSelections) return;
      }
      this.curr.selectedAddons.push(addon);
    }
    const addonSum = this.curr.selectedAddons.reduce((s, a) => s + a.price, 0);
    this.curr.totalPrice = (this.curr.service?.price || 0) + addonSum;
  }

  // ── Provider ──────────────────────────────────────────────────────────

  /** The add-ons the booking's offer makes free (an add-on-only promotion). */
  get creditAddonIds(): string[] {
    return this.bookingCredit?.kind === 'promotion' ? (this.bookingCredit.enhancementOptionIds || []) : [];
  }

  /** Is this add-on the one the booking's offer frees, on this service? */
  creditFreesAddon(addonId: string | number): boolean {
    // Only while the offer is still unclaimed by an earlier service in the cart.
    return this.creditAddonIds.includes(String(addonId)) && this._creditServiceOk(this.curr.service) && this.creditItemIndex === -1;
  }

  /** The item's selected add-on the offer is for, if picked. */
  private _creditAddon(it: { selectedAddons?: Array<{ id: string | number; price: number }> }): { id: string | number; price: number } | null {
    const ids = this.creditAddonIds;
    if (!ids.length) return null;
    return (it.selectedAddons || []).find(a => ids.includes(String(a.id))) || null;
  }

  /** Is the service within the credit's service / category scope (empty = any)? */
  private _creditServiceOk(svc: { id: number | string } | null | undefined): boolean {
    const c = this.bookingCredit;
    if (!c || !svc) return false;
    if (c.serviceIds.includes(Number(svc.id))) return true;
    if (c.serviceIds.length === 0 && !(c.categoryIds || []).length) return true;
    const cats = (c.categoryIds || []).map(String);
    if (cats.length) {
      return this.categories.some(cat => cats.includes(String(cat.id)) && cat.services.some(sv => String(sv.id) === String(svc.id)));
    }
    return false;
  }

  /** Does the booking's package / membership session cover this service? */
  creditCovers(svc: { id: number | string } | null | undefined, it?: { selectedAddons?: Array<{ id: string | number; price: number }> }): boolean {
    const c = this.bookingCredit;
    if (!c || !svc) return false;
    // An add-on-only offer covers the item only once its add-on is PICKED (the
    // server refuses the credit otherwise — the booking would go through, the
    // offer would not), and only on a service its terms allow.
    if (this.creditAddonIds.length) return this._creditServiceOk(svc) && !!this._creditAddon(it || this.curr);
    if (c.serviceIds.includes(Number(svc.id))) return true;
    if (c.serviceIds.length === 0 && !(c.categoryIds || []).length) return true;
    // An offer scoped to categories covers every service in them.
    const cats = (c.categoryIds || []).map(String);
    if (cats.length) {
      return this.categories.some(cat => cats.includes(String(cat.id)) && cat.services.some(sv => String(sv.id) === String(svc.id)));
    }
    return false;
  }

  /** What the booking's OFFER takes off this item (0 for a session credit —
   *  a session covers the whole line instead). */
  creditDiscount(it: { service: { id: number | string } | null; totalPrice: number; selectedAddons?: Array<{ id: string | number; price: number }> }): number {
    const c = this.bookingCredit;
    if (!c || c.kind !== 'promotion') return 0;
    let base = 0;
    if (this.creditAddonIds.length) {
      // An add-on-only offer: the discount is on the add-on's price, when picked.
      if (!this._creditServiceOk(it.service)) return 0;
      const a = this._creditAddon(it);
      if (!a) return 0;
      base = Number(a.price || 0);
    } else {
      if (!this.creditCovers(it.service, it)) return 0;
      base = Number(it.totalPrice || 0);
    }
    if (base <= 0) return 0;
    const v = Number(c.discountValue || 0);
    const d = c.discountType === 'percent' ? Math.round(base * v) / 100 : Math.min(v, base);
    return Math.max(0, Math.round(d * 100) / 100);
  }

  /** A PERCENT offer covers every service of the client's it applies to (Tim,
   *  2026-11-03 — the server's credit_reservations.spreads); a dollar amount
   *  or a free service / add-on applies to ONE item. */
  get creditSpreads(): boolean {
    const c = this.bookingCredit;
    return !!c && c.kind === 'promotion' && c.discountType === 'percent'
      && !['free_service', 'free_enhancement'].includes(c.rewardKind || '');
  }

  /** The item of the cart an OFFER is booked on — the first it covers. -1 =
   *  none yet. A session credit (package / membership) may cover several. */
  get creditItemIndex(): number {
    if (this.bookingCredit?.kind !== 'promotion') return -1;
    return this.items.findIndex(it => this.creditCovers(it.service, it) && this.creditDiscount(it) > 0);
  }

  /** The offer's discount on the cart item at `idx` (0 unless it is the one). */
  creditDiscountAt(idx: number): number {
    if (this.bookingCredit?.kind === 'promotion') {
      const it = this.items[idx];
      if (!it) return 0;
      // A guest's service is never on the client's offer.
      if (this.creditSpreads) return Number(it.guestIndex || 0) === 0 ? this.creditDiscount(it) : 0;
      return idx === this.creditItemIndex ? this.creditDiscount(it) : 0;
    }
    return 0;
  }

  /** Is the cart item at `idx` covered by a session credit? */
  creditCoversAt(idx: number): boolean {
    const it = this.items[idx];
    return !!it && !!this.bookingCredit && this.bookingCredit.kind !== 'promotion' && this.creditCovers(it.service, it);
  }

  /** What the whole cart costs, before any credit. */
  get cartTotal(): number {
    return Math.round(this.items.reduce((sum, it) => sum + Number(it.totalPrice || 0), 0) * 100) / 100;
  }

  /** What the cart costs after the offer / sessions. */
  get cartTotalAfterCredit(): number {
    return Math.round(this.items.reduce((sum, it, i) => sum + (this.creditCoversAt(i) ? 0 : Number(it.totalPrice || 0) - this.creditDiscountAt(i)), 0) * 100) / 100;
  }

  /** Total minutes of the whole cart (every guest; a guest's services run back to back). */
  get cartDuration(): number {
    return this.items.reduce((sum, it) => sum + this._itemDuration(it), 0);
  }

  /** The item's price after the booking's offer. */
  creditPrice(it: { service: { id: number | string } | null; totalPrice: number; selectedAddons?: Array<{ id: string | number; price: number }> }): number {
    return Math.max(0, Math.round((Number(it.totalPrice || 0) - this.creditDiscount(it)) * 100) / 100);
  }

  /** Start a portal booking with a package / membership session attached. */
  startBookingWithCredit(credit: BookingCredit): void {
    this.rebookSource = null;
    this.startBooking(false);
    this.bookingCredit = credit;
  }

  get filteredProviders(): Provider[] {
    const svc = this.curr.service;
    const addons = this.curr.selectedAddons;
    const locId = this.selectedLocationId;
    return this.providers.filter(p => {
      // Virtual filter providers (Anyone/Male/Female) bypass location filter
      const isFilterProvider = typeof p.id === 'string' && String(p.id).startsWith('prov-');
      // Location filter: when a location is selected, only show providers assigned to it.
      // A provider with NO explicit location assignment is available at all locations
      // (otherwise a single-location spa, or any not-yet-assigned provider, shows nothing).
      if (locId && !isFilterProvider && p.locationIds && p.locationIds.length > 0) {
        if (!p.locationIds.includes(locId)) return false;
      }
      const svcIds = p.serviceIds;
      if (svcIds) {
        const canService = svcIds.includes('all') || (svc !== null && svcIds.some(sid => String(sid) === String(svc.id)));
        if (!canService) return false;
      }
      const enhIds = p.enhancementIds;
      if (enhIds && !enhIds.includes('all')) {
        if (!addons.every(a => enhIds.some(eid => String(eid) === String(a.id)))) return false;
      }
      // A membership limited to certain providers: only they may deliver the
      // credited service (the server refuses anyone else).
      const cs = this.bookingCredit?.staffIds || [];
      if (cs.length && !isFilterProvider && this.creditCovers(svc) && !cs.includes(Number(p.id))) return false;
      return true;
    });
  }

  /** The provider list for THIS service: 'Anyone' / 'Female only' / 'Male only'
   *  carry the union of the days of the real providers they stand for here —
   *  not the server's union over every provider at the spa (Tim, 2026-10-23:
   *  one person does Product Pickup, Tue/Wed/Fri/Sat, and 'Anyone' listed all
   *  seven days). The date step then follows the same days (`pickProvider`). */
  get providersForStep(): Provider[] {
    const list = this.filteredProviders;
    const real = list.filter(p => !p.type?.startsWith('filter'));
    return list.map(p => {
      if (!p.type?.startsWith('filter')) return p;
      const g = p.type === 'filter-female' ? 'female' : p.type === 'filter-male' ? 'male' : '';
      const pool = g ? real.filter(r => (r.gender || '').toLowerCase() === g) : real;
      return { ...p, workDays: unionWorkDays(pool) };
    });
  }

  pickProvider(provId: string | number): void {
    // A filter pseudo-provider is taken with the days of the people it stands
    // for on this service, so the calendar offers only those days.
    const p = this.providersForStep.find(pr => String(pr.id) === String(provId))
      || this.providers.find(pr => String(pr.id) === String(provId));
    if (!p) return;
    this.curr.provider = p;
    this.items.push({ ...this.curr, selectedAddons: [...this.curr.selectedAddons], guestIndex: this.currentGuestIndex } as BookingItem);

    // The portal gets the same cart as the public flow (Tim, 2026-11-01 — a
    // signed-in client could only book ONE service per appointment, so two
    // services became two appointments with the provider's buffer between
    // them, and the offer rode on only one). Single-service spas skip it.
    if (!this.allowMultipleServices) {
      this.initDatetime();
      this.navigate('book-datetime');
    } else {
      this.navigate('book-addAnother');
    }
  }

  // ── Multi-service cart ────────────────────────────────────────────────

  removeItem(idx: number): void {
    this.items.splice(idx, 1);
  }

  /** The add-another prompt's follow-up ("for you or for someone else?") is
   *  showing. Reset whenever the step renders fresh. */
  addAnotherWho = false;

  /** May "Yes, add another" offer "for someone else"? Mirrors the group-size
   *  selector's own gates, plus: only while ON the last guest (appending keeps
   *  the person-by-person walk sequential) and only when nothing already in
   *  the cart is excluded from group bookings. */
  get canAddAnotherPerson(): boolean {
    if (!this.allowGroupBookings || this.entryMode !== 'booking') return false;
    if (this.groupSize >= this.maxGroupSize) return false;
    if (this.currentGuestIndex !== this.groupSize - 1) return false;
    if (this.groupBookingLocationIds.length > 0
        && (this.selectedLocationId == null
            || !this.groupBookingLocationIds.includes(this.selectedLocationId))) return false;
    if (this.groupBookingExcludedServiceIds.length > 0
        && this.items.some(it => it.service && this.groupBookingExcludedServiceIds.includes(Number(it.service.id)))) return false;
    return true;
  }

  addAnotherService(): void {
    this.addAnotherWho = false;
    this.curr = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
    this.expandedCat = null;
    this.navigate('book-categories');
  }

  /** "Yes, and it's for someone else": grow the group by one and start picking
   *  services for the new person — exactly as if they had chosen N+1 people
   *  up front. Mirrors setGroupSize's effects incrementally. */
  addAnotherForNewGuest(): void {
    this.addAnotherWho = false;
    this.groupSize += 1;
    this.guests[this.groupSize - 1] = { firstName: '', lastName: '', phone: '', email: '' };
    this.bookForOther = true;
    this.currentGuestIndex = this.groupSize - 1;
    this.curr = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
    this.expandedCat = null;
    this.navigate('book-categories');
  }

  setGroupSize(size: number): void {
    this.groupSize = size;
    this.currentGuestIndex = 0;
    // Initialize guest entries (index 0 = booker, uses formData; 1..N-1 for additional people)
    this.guests = [];
    for (let i = 1; i < size; i++) {
      this.guests[i] = { firstName: '', lastName: '', phone: '', email: '' };
    }
    // Force "book for other" on for groups
    if (size > 1) {
      this.bookForOther = true;
    }
  }

  continueToNextPerson(): void {
    this.currentGuestIndex++;
    this.curr = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
    this.expandedCat = null;
    this.step = 'book-categories';
  }

  /** Returns provider IDs already booked by OTHER guest indices, with their time ranges */
  get usedProviderSlots(): Map<string, { start: number; end: number }[]> {
    const map = new Map<string, { start: number; end: number }[]>();
    if (!this.selectedTime) return map;
    const baseMins = this._parseTimeToMins(this.selectedTime);
    const guestOffsets: Record<number, number> = {};

    for (const item of this.items) {
      const gi = item.guestIndex ?? 0;
      if (gi === this.currentGuestIndex) continue; // skip own items
      if (guestOffsets[gi] === undefined) guestOffsets[gi] = baseMins;
      const provKey = String(item.provider.id);
      const start = guestOffsets[gi];
      const end = start + this._itemDuration(item);
      guestOffsets[gi] = end;
      if (!map.has(provKey)) map.set(provKey, []);
      map.get(provKey)!.push({ start, end });
    }
    return map;
  }

  isProviderUsedByOtherGuest(provId: string | number): boolean {
    // Simple check: is this provider already picked by a different guest?
    // Works even before a time is selected (provider step comes before datetime).
    const key = String(provId);
    for (const item of this.items) {
      const gi = item.guestIndex ?? 0;
      if (gi !== this.currentGuestIndex && String(item.provider.id) === key) {
        return true;
      }
    }
    return false;
  }

  // ── Calendar ──────────────────────────────────────────────────────────

  getWeekStart(): Date {
    const d = new Date(this.today);
    d.setDate(d.getDate() + this.weekOffset * 7);
    return d;
  }

  prevWeek(): void {
    if (this.weekOffset > 0) this.weekOffset--;
  }

  nextWeek(): void {
    this.weekOffset++;
  }

  isDateToday(dateStr: string): boolean {
    return dateStr === this.today.toISOString().split('T')[0];
  }

  _isProviderOnTimeOff(provider: Provider, dateStr: string): boolean {
    if (!provider.timeOff?.length) return false;
    return provider.timeOff.some(t => dateStr >= t.startDate && dateStr <= t.endDate);
  }

  /**
   * Get service schedule restrictions for a provider+service on a given DOW.
   * Returns null if no restriction (available all hours), or an array of {start, end} ranges.
   */
  _getSvcSchedRanges(provider: Provider, svcId: string | number, dow: string): { start: string; end: string }[] | null {
    const svcScheds = provider.serviceSchedules;
    if (!svcScheds) return null;
    const entries = svcScheds[String(svcId)];
    if (!entries || entries.length === 0) return null; // no restriction for this service
    // Filter to this DOW
    const dowEntries = entries.filter(e => e.dayOfWeek === dow);
    if (dowEntries.length === 0) return []; // restricted but not on this day
    return dowEntries.map(e => ({ start: e.startTime, end: e.endTime }));
  }

  /** Intersect work day ranges with category schedule ranges. */
  _intersectRanges(
    workRanges: { start: string; end: string }[],
    catRanges: { start: string; end: string }[],
  ): { start: string; end: string }[] {
    const result: { start: string; end: string }[] = [];
    for (const w of workRanges) {
      for (const c of catRanges) {
        const start = w.start > c.start ? w.start : c.start;
        const end = w.end < c.end ? w.end : c.end;
        if (start < end) result.push({ start, end });
      }
    }
    return result;
  }

  _buildUnionWorkDays(): WorkDays {
    const unionWorkDays: WorkDays = {};
    for (const it of this.items) {
      if (!it.provider?.workDays) continue;
      for (const [dow, ranges] of Object.entries(it.provider.workDays)) {
        let effectiveRanges = ranges;
        if (it.service) {
          const svcRanges = this._getSvcSchedRanges(it.provider, it.service.id, dow);
          if (svcRanges !== null) {
            effectiveRanges = this._intersectRanges(ranges, svcRanges);
          }
        }
        if (!effectiveRanges.length) continue;
        if (!unionWorkDays[dow]) unionWorkDays[dow] = [];
        unionWorkDays[dow].push(...effectiveRanges);
      }
    }
    return unionWorkDays;
  }

  _buildUnionWorkDaysForDate(dateStr: string): WorkDays {
    const unionWorkDays: WorkDays = {};
    for (const it of this.items) {
      if (!it.provider?.workDays) continue;
      // Skip providers on time-off for this specific date
      if (this._isProviderOnTimeOff(it.provider, dateStr)) continue;
      for (const [dow, rawRanges] of Object.entries(it.provider.workDays)) {
        // Every-other-week shifts only count on their ON weeks for this date
        const ranges = rawRanges.filter(r => rangeAppliesOn(r, dateStr));
        if (!ranges.length) continue;
        let effectiveRanges = ranges;
        if (it.service) {
          const svcRanges = this._getSvcSchedRanges(it.provider, it.service.id, dow);
          if (svcRanges !== null) {
            effectiveRanges = this._intersectRanges(ranges, svcRanges);
          }
        }
        if (!effectiveRanges.length) continue;
        if (!unionWorkDays[dow]) unionWorkDays[dow] = [];
        unionWorkDays[dow].push(...effectiveRanges);
      }
    }
    return unionWorkDays;
  }

  isClosedDay(dateStr: string): boolean {
    const closed = this.availabilityConfig.closedDays;
    if (!closed?.length) return false;
    // A closure for ONE location (locationId) shuts only that location; one with
    // no locationId shuts every location (2026-10-30).
    return closed.some(cd =>
      (!cd.locationId || !this.selectedLocationId || cd.locationId === this.selectedLocationId)
      && (cd.endDate ? (dateStr >= cd.date && dateStr <= cd.endDate) : dateStr === cd.date));
  }

  get weekDays(): WeekDay[] {
    const start = this.getWeekStart();
    const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const blackoutHours = this.availabilityConfig.blackoutWindowHours ?? 0;
    const cutoffMs = Date.now() + blackoutHours * 60 * 60 * 1000;

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const isPast = d < this.today;
      const isToday = d.getTime() === this.today.getTime();

      // Closed day → no slots
      if (this.isClosedDay(dateStr)) {
        return { date: dateStr, dayNum: d.getDate(), dowLabel: DOW[d.getDay()].toUpperCase().slice(0, 3), isToday, isPast, hasSlots: false };
      }

      // Use date-aware work days that exclude providers on time-off
      const unionWorkDays = this._buildUnionWorkDaysForDate(dateStr);
      const noSchedule = Object.keys(unionWorkDays).length === 0;

      let hasSlots: boolean;
      if (isPast) {
        hasSlots = false;
      } else if (isToday) {
        // Still check time-off for today — all providers might be off
        if (noSchedule && this.items.some(it => it.provider?.workDays && Object.keys(it.provider.workDays).length > 0)) {
          hasSlots = false; // All providers on time-off today
        } else {
          hasSlots = true;
        }
      } else if (noSchedule) {
        // If we have items with providers that normally have schedules but are all on time-off, grey out
        const anyProviderHasSchedule = this.items.some(it => it.provider?.workDays && Object.keys(it.provider.workDays).length > 0);
        hasSlots = !anyProviderHasSchedule;
      } else {
        const s = getSlotsForDate(dateStr, unionWorkDays, this.availabilityConfig.intervalMinutes);
        const all = [...s.morning, ...s.afternoon, ...s.evening];
        hasSlots = blackoutHours > 0
          ? all.some(t => this._slotToMs(dateStr, t) > cutoffMs)
          : all.length > 0;
      }

      return { date: dateStr, dayNum: d.getDate(), dowLabel: DOW[d.getDay()].toUpperCase().slice(0, 3), isToday, isPast, hasSlots };
    });
  }

  get calMonthLabel(): string {
    return this.getWeekStart().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  get selectedDateLabel(): string {
    if (!this.selectedDate) return '';
    return new Date(this.selectedDate + 'T12:00:00')
      .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  /**
   * Does this date's availability come from the same-day ("Open Today") engine?
   * Settings → Same-day booking = "Open Today rules", the date is today, and a
   * single service (the engine books one service at a time).
   */
  usesSameDay(dateStr: string | null = this.selectedDate): boolean {
    if (!dateStr || this.availabilityConfig.sameDayMode !== 'open_today') return false;
    if (!this.isDateToday(dateStr)) return false;
    return this.items.length <= 1;
  }

  /** The same-day promo for the current date/service, once availability has loaded. */
  /** The quiet-hours discount on a time of the selected date, if any. */
  quietHoursFor(time: string): { pct: number; price: number; label: string } | null {
    if (!this.selectedDate) return null;
    // The same key the fetch stored under (not _availabilityKey, which lacks the gender/chain suffix).
    const key = this._slotRequest(this.selectedDate)?.cacheKey;
    const q = key ? this._quietHours.get(key) : null;
    const hit = q?.byTime[time];
    return hit ? { ...hit, label: q!.label } : null;
  }
  get quietHoursAny(): { label: string; price: number } | null {
    if (!this.selectedDate) return null;
    const key = this._slotRequest(this.selectedDate)?.cacheKey;
    const q = key ? this._quietHours.get(key) : null;
    return q && Object.keys(q.byTime).length ? { label: q.label, price: q.price } : null;
  }

  get sameDayPromo(): SameDayPromo | null {
    if (!this.usesSameDay()) return null;
    const key = this._availabilityKey(this.selectedDate!);
    return key ? (this._sameDayPromo.get(key) || null) : null;
  }

  private _availabilityKey(dateStr: string): string | null {
    const svc = this.curr.service || this.items[0]?.service;
    if (!svc) return null;
    const provider = this.curr.provider;
    const providerId = provider && typeof provider.id === 'number' ? provider.id : null;
    const addonIds = (this.curr.selectedAddons || []).map(a => a.id);
    return `${dateStr}|${svc.id}|${providerId || ''}|${addonIds.join(',')}|${this.selectedLocationId || ''}`;
  }

  get timePeriods(): TimePeriod[] {
    if (!this.selectedDate) return [];
    if (this.isClosedDay(this.selectedDate)) return [];

    // Under Open Today rules the server already decided what is bookable today
    // (therapists on-site, windows, lead time) — the client-side blackout must
    // not trim it again.
    const sameDay = this.usesSameDay(this.selectedDate);
    const blackoutHours = sameDay ? 0 : (this.availabilityConfig.blackoutWindowHours ?? 0);
    const cutoffMs = Date.now() + blackoutHours * 60 * 60 * 1000;
    const isToday = this.isDateToday(this.selectedDate);
    const unionWorkDays = this._buildUnionWorkDaysForDate(this.selectedDate);

    if (!Object.keys(unionWorkDays).length) {
      if (isToday) return [{ label: this._currentPeriodLabel(), range: '', slots: [], sameDayMsg: this.availabilityConfig.sameDayMessage }];
      return [];
    }
    if (isToday && this.availabilityConfig.sameDayMode === 'off') {
      return [{ label: this._currentPeriodLabel(), range: '', slots: [], sameDayMsg: this.availabilityConfig.sameDayMessage }];
    }

    // Wait for API availability before showing any slots (prevents flash of unfiltered times)
    const apiSlots = this._currentSlotSet;
    const hasApi = this.apiBase && this.apiBase !== 'mock';
    if (hasApi && !apiSlots) {
      return [];  // still loading — show nothing until API responds
    }

    const raw = getSlotsForDate(this.selectedDate, unionWorkDays, this.availabilityConfig.intervalMinutes);
    const periods: TimePeriod[] = [];
    let anyBlackedOut = false;

    for (const [label, slotList] of [
      ['MORNING',   raw.morning],
      ['AFTERNOON', raw.afternoon],
      ['EVENING',   raw.evening],
    ] as [string, string[]][]) {
      if (!slotList.length) continue;
      let available = blackoutHours > 0
        ? slotList.filter(t => this._slotToMs(this.selectedDate!, t) > cutoffMs)
        : [...slotList];
      if (available.length < slotList.length) anyBlackedOut = true;
      // Filter against real availability from API
      if (apiSlots) {
        available = available.filter(t => apiSlots.has(t));
      }
      if (available.length) {
        periods.push({ label, range: available[0] + ' – ' + available[available.length - 1], slots: available, sameDayMsg: null });
      }
    }

    // The same-day message ('Nothing is available on the selected date…')
    // only when NOTHING today is bookable. It used to show whenever the
    // same-day lead time hid some of today's earlier times, sitting right
    // above the times still open (Tim, 2026-11-03).
    if (periods.length === 0 && (anyBlackedOut || isToday)) {
      periods.unshift({ label: this._currentPeriodLabel(), range: '', slots: [], sameDayMsg: this.availabilityConfig.sameDayMessage });
    }

    return periods;
  }

  _currentPeriodLabel(): string {
    const h = new Date().getHours();
    return h < 12 ? 'MORNING' : h < 17 ? 'AFTERNOON' : 'EVENING';
  }

  _slotToMs(dateStr: string, timeStr: string): number {
    const mins = this._parseTimeToMins(timeStr);
    const [y, mo, d] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, d, Math.floor(mins / 60), mins % 60).getTime();
  }

  setDate(dateStr: string): void {
    this.selectedDate = dateStr;
    this.selectedTime = null;
    if (this.isDateToday(dateStr) && ((this.availabilityConfig.blackoutWindowHours ?? 0) > 0 || this.usesSameDay(dateStr))) {
      this.expandedPeriod = this._currentPeriodLabel();
    } else {
      this.expandedPeriod = null;
    }
    this._fetchAvailableSlots(dateStr);
  }

  /** 'female' | 'male' when a gender filter pseudo-provider is selected. */
  private _providerGender(p: { type?: string } | null | undefined): 'female' | 'male' | null {
    if (p?.type === 'filter-female') return 'female';
    if (p?.type === 'filter-male') return 'male';
    return null;
  }

  /** The whole cart for the availability `chain` param — every service must be
   *  performable at its own chained time, or the slot grid lies (a Sunday was
   *  offered for a service nobody on Sunday could do; the client learned at the
   *  very end of the form). null until the cart actually chains. */
  private _availabilityChain(): import('./api').ChainItem[] | null {
    if (this.items.length < 2) return null;
    return this.items.map(it => ({
      serviceId: it.service.id,
      staffId: typeof it.provider?.id === 'number' ? Number(it.provider.id) : undefined,
      gender: this._providerGender(it.provider) || undefined,
      addonIds: it.selectedAddons.map(a => a.id),
      guestIndex: it.guestIndex ?? 0,
    }));
  }

  private _chainCacheSuffix(): string {
    const chain = this._availabilityChain();
    return chain ? '|' + JSON.stringify(chain) : '';
  }

  /** What the slot grid is FOR. The slot a client picks is item 0's start time
   *  (itemStartTimes chains everything else off it), so the grid must be
   *  anchored to the FIRST item in the cart — its service, its provider, its
   *  addons. It used to prefer `curr`, which at the datetime step still holds
   *  the LAST service picked, so a multi-service grid could be drawn for the
   *  wrong service entirely. `curr` remains the fallback for flows that reach
   *  the grid before anything is in the cart. */
  private _slotRequest(dateStr: string) {
    const first = this.items[0];
    const svc = first?.service || this.curr.service;
    if (!svc) return null;
    const provider = first?.provider || this.curr.provider;
    // Filter providers (prov-any, prov-f, prov-m) are virtual — don't send as provider_id
    const providerId = provider && typeof provider.id === 'number' ? provider.id : null;
    const addonIds = ((first ? first.selectedAddons : this.curr.selectedAddons) || []).map(a => a.id);
    const gender = this._providerGender(provider);
    const cacheKey = `${dateStr}|${svc.id}|${providerId || ''}|${addonIds.join(',')}|${this.selectedLocationId || ''}|${gender || ''}${this._chainCacheSuffix()}${this.rescheduleBookingId ? `|r${this.rescheduleBookingId}` : ''}`;
    return { svc, providerId, addonIds, gender, cacheKey };
  }

  private _fetchAvailableSlots(dateStr: string): void {
    if (!this.apiBase || this.apiBase === 'mock') return;
    // Check that we have at least one service (from items or current selection)
    if (!this.items.length && !this.curr.service) return;

    const req = this._slotRequest(dateStr);
    if (!req) return;
    const { svc, providerId, addonIds, gender, cacheKey } = req;

    // Skip if already cached
    if (this._availableSlots.has(cacheKey)) return;

    this._slotsFailed.delete(cacheKey);
    this._slotsLoading = true;
    const sameDay = this.usesSameDay(dateStr);
    fetchAvailability(
      this.apiBase, dateStr, svc.id,
      providerId, addonIds,
      this.selectedLocationId,
      sameDay,
      this._availabilityChain(),
      gender,
      this.rescheduleBookingId,
    ).then(res => {
      const slots = res.slots;
      const timeSet = new Set(slots.map(s => s.time));
      this._availableSlots.set(cacheKey, timeSet);
      if (sameDay) this._sameDayPromo.set(cacheKey, res.promo || null);
      // Quiet-hours pricing: which of these times are discounted, and by how much.
      const qh = res.quietHours;
      if (qh) {
        const byTime: Record<string, { pct: number; price: number }> = {};
        for (const sl of slots as any[]) if (sl.quietHoursPct) byTime[sl.time] = { pct: sl.quietHoursPct, price: sl.discountedPrice };
        this._quietHours.set(cacheKey, { label: qh.label, price: qh.price, byTime });
      } else {
        this._quietHours.delete(cacheKey);
      }
      this._slotsLoading = false;
      // Funnel granularity: which date did they look at, and was there anything
      // to book? (offsetDays 0 = today). Fires once per date/service combo.
      trackWidgetEvent('datetime_view', {
        date: dateStr,
        offsetDays: this.dateOffsetDays(dateStr),
        slotCount: timeSet.size,
        serviceId: svc.id,
        sameDay: sameDay || undefined,
      });
      if (this._onChangeCallback) this._onChangeCallback();
    }).catch(() => {
      this._slotsLoading = false;
      this._slotsFailed.add(cacheKey);
      if (this._onChangeCallback) this._onChangeCallback();
    });
  }

  /** The selected date's times are still on their way from the server (Tim,
   *  2026-11-03): `timePeriods` is empty while it waits, which the date step
   *  used to draw as 'Sorry! No times are available' — long enough on a
   *  reschedule that people gave up on a date that had times. */
  get slotsPending(): boolean {
    if (!this.selectedDate || !this.apiBase || this.apiBase === 'mock') return false;
    if (this.isClosedDay(this.selectedDate)) return false;
    if (!Object.keys(this._buildUnionWorkDaysForDate(this.selectedDate)).length) return false;
    const req = this._slotRequest(this.selectedDate);
    if (!req) return false;
    return !this._availableSlots.has(req.cacheKey) && !this._slotsFailed.has(req.cacheKey);
  }

  /** The selected date's availability request failed. */
  get slotsFailed(): boolean {
    if (!this.selectedDate) return false;
    const req = this._slotRequest(this.selectedDate);
    return !!req && this._slotsFailed.has(req.cacheKey) && !this._availableSlots.has(req.cacheKey);
  }

  /** Ask for the selected date's times again (after a failure). */
  retrySlots(): void {
    if (this.selectedDate) this._fetchAvailableSlots(this.selectedDate);
  }

  get _currentSlotSet(): Set<string> | null {
    if (!this.selectedDate || !this.apiBase || this.apiBase === 'mock') return null;
    const req = this._slotRequest(this.selectedDate);
    if (!req) return null;
    return this._availableSlots.get(req.cacheKey) || null;
  }

  initDatetime(): void {
    this.selectedDate = this.today.toISOString().split('T')[0];
    this.selectedTime = null;
    this.weekOffset = 0;
    this.expandedPeriod = this._currentPeriodLabel();
    this._fetchAvailableSlots(this.selectedDate);
  }

  togglePeriod(label: string): void {
    this.expandedPeriod = this.expandedPeriod === label ? null : label;
  }

  pickTime(slot: string): void {
    this.selectedTime = slot;
  }

  get itemStartTimes(): string[] {
    if (!this.selectedTime || !this.items.length) return [];
    const baseMins = this._parseTimeToMins(this.selectedTime);
    // Track accumulated time per guest index
    const guestOffsets: Record<number, number> = {};
    return this.items.map(item => {
      const gi = item.guestIndex ?? 0;
      if (guestOffsets[gi] === undefined) guestOffsets[gi] = baseMins;
      const start = this._minsToTimeStr(guestOffsets[gi]);
      guestOffsets[gi] += this._itemDuration(item);
      return start;
    });
  }

  // ── Contact form ──────────────────────────────────────────────────────

  get needsHomeAddress(): boolean {
    return this.items.some(item => item.service.requireHomeAddress);
  }

  get processedFormFields(): ProcessedEntry[] {
    const fields = this.formConfig.fields;
    const out: ProcessedEntry[] = [];
    let i = 0;
    while (i < fields.length) {
      if (fields[i].half && fields[i + 1]?.half) {
        out.push({ type: 'pair', key: fields[i].id + '-pair', fields: [fields[i], fields[i + 1]] });
        i += 2;
      } else {
        out.push({ type: 'single', key: fields[i].id, field: fields[i] });
        i++;
      }
    }
    return out;
  }

  processedSubFields(subFields: FormField[]): ProcessedEntry[] {
    const out: ProcessedEntry[] = [];
    let i = 0;
    while (i < subFields.length) {
      if (subFields[i].half && subFields[i + 1]?.half) {
        out.push({ type: 'pair', key: subFields[i].id + '-pair', fields: [subFields[i], subFields[i + 1]] });
        i += 2;
      } else {
        out.push({ type: 'single', key: subFields[i].id, field: subFields[i] });
        i++;
      }
    }
    return out;
  }

  /** Book for someone else on the PUBLIC form: no phone for the other person
   *  (or the booker's own number typed in) — the person is booked as a
   *  placeholder and the booker must take responsibility (Tim, 2026-11-03).
   *  The server applies the same rule. */
  get otherPhoneMissing(): boolean {
    const digits = (v: unknown) => String(v ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
    const other = digits(this.formData.otherPhone);
    return !other || other === digits(this.formData.phone);
  }

  get formErrors(): Record<string, string> {
    const e: Record<string, string> = {};
    if (this.formData.forOther && this.groupSize <= 1 && !this.bookForOther
        && (String(this.formData.otherFirstName || '').trim() || String(this.formData.otherLastName || '').trim())
        && this.otherPhoneMissing && !this.formData.otherResponsibility) {
      e.otherResponsibility = 'Add their phone number, or turn this on';
    }
    // First name, last name, phone and email are ALWAYS required for a booking —
    // you cannot serve or contact a client without them. validatePhone/
    // validateEmail treat an empty string as "no error" (they only flag a
    // malformed value), and name was never checked at all, so a blank contact
    // form used to satisfy canProceed and submit. That produced waitlist/booking
    // rows with no client name, phone or email. Require non-empty here, then run
    // the format check on top.
    const blank = (v: unknown) => !((v as string) || '').trim();
    // A recognised returning client books on their phone number alone — the
    // server already has their name and email (the collapsed form never
    // rendered those fields, so requiring them would dead-end the step).
    const known = this.isKnownClient;
    if (!known && blank(this.formData.firstName)) e.firstName = 'Required';
    if (!known && blank(this.formData.lastName)) e.lastName = 'Required';
    if (blank(this.formData.phone)) e.phone = 'Enter your phone number';
    else { const phoneErr = validatePhone((this.formData.phone as string) || ''); if (phoneErr) e.phone = phoneErr; }
    if (!known && blank(this.formData.email)) e.email = 'Enter your email';
    else if (!known) { const emailErr = validateEmail((this.formData.email as string) || ''); if (emailErr) e.email = emailErr; }
    // "Booking for someone else": the recipient's contact is required too. The
    // live fields are the top-level other* props (data-field="otherFirstName"…),
    // not formData.other* — the old formData.forOther check never ran, so a
    // book-for-someone-else could submit with a blank recipient.
    // GROUPS are excluded: setGroupSize forces bookForOther on, but the group
    // contact step renders guest sections, never the other* recipient fields —
    // so this block demanded four fields that were nowhere on the screen and
    // every group booking failed validation forever.
    if (this.bookForOther && this.groupSize <= 1) {
      if (blank(this.otherFirstName)) e.otherFirstName = 'Required';
      if (blank(this.otherLastName)) e.otherLastName = 'Required';
      // Their phone and email are asked for, not required (Tim, 2026-11-03):
      // without a phone they are booked as a placeholder and the booker takes
      // responsibility (the toggle below is always required here).
      if (!blank(this.otherPhone)) { const opErr = validatePhone(this.otherPhone || ''); if (opErr) e.otherPhone = opErr; }
      if (!blank(this.otherEmail)) { const oeErr = validateEmail(this.otherEmail || ''); if (oeErr) e.otherEmail = oeErr; }
    }
    // Validate required radio/select fields (e.g. SMS opt-in). Not rendered
    // for a recognised client — their consent and address are already on
    // file from the visit that created the record.
    for (const field of this.formConfig.fields) {
      if (known) break;
      if (field.required && field.type === 'radio' && !this.formData[field.id]) {
        e[field.id] = 'Please select an option';
      }
    }
    // Validate address fields when required
    if (this.needsHomeAddress && !known) {
      if (!((this.formData.address1 as string) || '').trim()) e.address1 = 'Required';
      if (!((this.formData.city as string) || '').trim()) e.city = 'Required';
      if (!((this.formData.state as string) || '').trim()) e.state = 'Required';
      if (!((this.formData.zip as string) || '').trim()) e.zip = 'Required';
    }
    // Validate group guest fields
    for (let gi = 1; gi < this.groupSize; gi++) {
      const g = this.guests[gi];
      if (!g) continue;
      const gPhoneErr = validatePhone(g.phone || '');
      if (gPhoneErr) e[`guest${gi}Phone`] = gPhoneErr;
      const gEmailErr = validateEmail(g.email || '');
      if (gEmailErr) e[`guest${gi}Email`] = gEmailErr;
    }
    return e;
  }

  /** Why Continue is blocked, in the client's words — one entry per fix,
   *  each pointing at the field to scroll to. Powers the tap-to-see-why
   *  summary: a greyed-out button with no reasons is a dead end, and the
   *  blockers are usually scrolled far off screen. */
  get blockedReasons(): { label: string; field: string }[] {
    // The deposit step — the ONLY gated step a PORTAL booking meets (it
    // skips the contact form) — was a silently disabled button: card half
    // complete, policy toggle unticked, nothing anywhere saying so. A
    // client called it "it won't let me continue" (2026-09-01).
    if (this.step === 'book-confirm' && this.bookForOther) {
      const out: { label: string; field: string }[] = [];
      if (!this.otherFirstName.trim()) out.push({ label: "Enter their first name", field: 'otherFirstName' });
      if (!this.otherLastName.trim()) out.push({ label: "Enter their last name", field: 'otherLastName' });
      if (!this.responsibleAgreed) out.push({ label: 'Turn on "I accept responsibility for this booking"', field: 'toggleResponsible' });
      return out;
    }
    if (this.step === 'book-payment') {
      const out: { label: string; field: string }[] = [];
      if (!this.stripePublishableKey) {
        // Payments misconfigured: card entry can never complete. The text-me
        // path still works — point at it instead of an unfillable form.
        out.push({ label: 'Card entry is not available here — tap "Text me a payment link instead" below', field: 'depositTextMe' });
      } else if (!stripeCardComplete()) {
        out.push({ label: 'Complete your card details — number, expiry, CVC and ZIP', field: 'depositCard' });
      }
      if (this.deposit?.policy?.required && !this.depositPolicyAgreed) {
        out.push({ label: 'Agree to the cancellation policy', field: 'depositPolicyAgreed' });
      }
      if (this.depositGcNumber.trim() && !this.depositGcAgreed) {
        out.push({ label: 'Tick the gift card agreement (or clear the number)', field: 'depositGcNumber' });
      }
      return out;
    }
    if (this.step !== 'book-contact') return [];
    const out: { label: string; field: string }[] = [];
    const errs = this.formErrors;
    const labels: Record<string, string> = {
      firstName: 'Your first name', lastName: 'Your last name',
      phone: 'A valid phone number', email: 'A valid email address',
      otherFirstName: "The recipient's first name", otherLastName: "The recipient's last name",
      otherPhone: "The recipient's phone number", otherEmail: "The recipient's email",
    };
    let addressAdded = false;
    for (const key of Object.keys(errs)) {
      if (['address1', 'city', 'state', 'zip'].includes(key)) {
        if (!addressAdded) { out.push({ label: 'Your home address', field: 'address1' }); addressAdded = true; }
        continue;
      }
      const gm = key.match(/^guest(\d+)(Phone|Email)$/);
      if (gm) { out.push({ label: `Guest ${gm[1]}'s ${gm[2].toLowerCase()}`, field: key }); continue; }
      if (labels[key]) { out.push({ label: labels[key], field: key }); continue; }
      const cfg = this.formConfig.fields.find(f => f.id === key);
      out.push({ label: cfg?.label || 'A required field above', field: key });
    }
    // Guests with blank details produce no formErrors entries — guestsValid
    // covers them separately.
    for (let gi = 1; gi < this.groupSize; gi++) {
      const g = this.guests[gi];
      if (!g || !g.firstName.trim() || !g.lastName.trim() || !g.phone.trim() || !g.email.trim()) {
        if (!out.some(r => r.field === `guest${gi}FirstName`)) {
          out.push({ label: `Guest ${gi}'s details`, field: `guest${gi}FirstName` });
        }
      }
    }
    const hasConditions = !!(this.cancellationPolicyHtml && this.cancellationPolicyHtml.replace(/<[^>]*>/g, '').trim())
      || (this.formConfig.notices || []).some(n => n.html && n.html.replace(/<[^>]*>/g, '').trim());
    if (hasConditions && !this.agreedToPolicy) {
      out.push({ label: 'Agree to the booking conditions', field: 'agreedToPolicy' });
    }
    return out;
  }

  get guestsValid(): boolean {
    for (let gi = 1; gi < this.groupSize; gi++) {
      const g = this.guests[gi];
      if (!g || !g.firstName.trim() || !g.lastName.trim() || !g.phone.trim() || !g.email.trim()) return false;
      if (this.formErrors[`guest${gi}Phone`] || this.formErrors[`guest${gi}Email`]) return false;
    }
    return true;
  }

  // ── Promo code ────────────────────────────────────────────────────────

  // (promo validation is done externally in api.ts and sets promoStatus/promoOffer/promoError)

  // ── Gift card ─────────────────────────────────────────────────────────

  get gcErrors(): Record<string, string> {
    const e: Record<string, string> = {};
    const amt = Number(this.gcAmount);
    if (!this.gcSelectedPromotion && this.gcAmount !== '' && (isNaN(amt) || amt < this.gcConfig.minAmount || amt > this.gcConfig.maxAmount)) {
      e.amount = `Enter an amount between $${this.gcConfig.minAmount} and $${this.gcConfig.maxAmount}`;
    }
    const phoneErr = validatePhone(this.gcYourDetails.phone || '');
    if (phoneErr) e.yourPhone = phoneErr;
    const emailErr = validateEmail(this.gcYourDetails.email || '');
    if (emailErr) e.yourEmail = emailErr;
    if (this.gcPurchaseFor === 'someone') {
      const reErr = validateEmail(this.gcRecipient.email || '');
      if (reErr) e.recipientEmail = reErr;
    }
    return e;
  }

  /** What still needs filling in on the gift-card details step, in page
   *  order: [fieldId, label]. The button is never a dead disabled control —
   *  tapping it with gaps lists these and highlights the fields. */
  get gcMissingDetails(): Array<[string, string]> {
    const out: Array<[string, string]> = [];
    const amt = Number(this.gcAmount);
    if (!this.gcSelectedPromotion) {
      if (!this.gcAmount || isNaN(amt)) out.push(['gcAmount', 'Amount']);
      else if (amt < this.gcConfig.minAmount || amt > this.gcConfig.maxAmount) out.push(['gcAmount', `Amount ($${this.gcConfig.minAmount}–$${this.gcConfig.maxAmount})`]);
    }
    if (this.gcPurchaseFor === 'someone') {
      if (!this.gcRecipient.firstName.trim()) out.push(['gcRecipientFirstName', "Recipient's first name"]);
      if (!this.gcRecipient.lastName.trim()) out.push(['gcRecipientLastName', "Recipient's last name"]);
      if (!this.gcRecipient.email) out.push(['gcRecipientEmail', "Recipient's email"]);
      else if (this.gcErrors.recipientEmail) out.push(['gcRecipientEmail', "Recipient's email (check the address)"]);
    }
    if (!this.gcYourDetails.firstName.trim()) out.push(['gcYourFirstName', 'Your first name']);
    if (!this.gcYourDetails.lastName.trim()) out.push(['gcYourLastName', 'Your last name']);
    const phone = (this.gcYourDetails.phone || '').replace(/\D/g, '');
    if (!phone) out.push(['gcYourPhone', 'Your phone number']);
    else if (phone.length !== 10) out.push(['gcYourPhone', 'Your phone number (10 digits)']);
    if (!this.gcYourDetails.email) out.push(['gcYourEmail', 'Your email']);
    else if (this.gcErrors.yourEmail) out.push(['gcYourEmail', 'Your email (check the address)']);
    return out;
  }

  /** Set when Continue was tapped with gaps — the form then shows them. */
  gcShowMissing: boolean = false;

  /** Gift card "Your details" is phone-first, like the booking surfaces:
   *  name + email unfold once a full number is in (or were prefilled). */
  gcRememberedFirstName = '';
  get gcYourDetailsExpanded(): boolean {
    const d = this.gcYourDetails;
    if (d.firstName || d.lastName || d.email) return true;
    return (d.phone || '').replace(/\D/g, '').length >= 10;
  }

  prefillGcFromDevice(): void {
    // Signed in (buying from the portal): the buyer is the account holder.
    if (this.clientId && this.client) {
      const c = this.client, d = this.gcYourDetails;
      if (c.firstName && !d.firstName) d.firstName = c.firstName;
      if (c.lastName && !d.lastName) d.lastName = c.lastName;
      if (c.phone && !d.phone) d.phone = c.phone;
      if (c.email && !d.email) d.email = c.email;
      return;
    }
    if (!this.rememberOnDevice || this.clientId) return;
    const r = loadRemembered(this.apiBase);
    if (!r) { this.gcRememberedFirstName = ''; return; }
    const d = this.gcYourDetails;
    if (r.firstName && !d.firstName) d.firstName = r.firstName;
    if (r.lastName && !d.lastName) d.lastName = r.lastName;
    if (r.phone && !d.phone) d.phone = r.phone;
    if (r.email && !d.email) d.email = r.email;
    this.gcRememberedFirstName = r.firstName || '';
    trackWidgetEvent('device_prefill', { entryMode: 'giftcard' });
  }

  forgetGcDevice(): void {
    forgetRemembered(this.apiBase);
    this.gcRememberedFirstName = '';
    this.gcYourDetails = { firstName: '', lastName: '', email: '', phone: '' };
  }

  get gcCanProceedDetails(): boolean {
    const amt = Number(this.gcAmount);
    if (!this.gcSelectedPromotion) {
      if (!this.gcAmount || isNaN(amt) || amt < this.gcConfig.minAmount || amt > this.gcConfig.maxAmount) return false;
    }
    if (!this.gcYourDetails.firstName.trim() || !this.gcYourDetails.lastName.trim()) return false;
    const phone = (this.gcYourDetails.phone || '').replace(/\D/g, '');
    if (!phone || phone.length !== 10) return false;
    if (!this.gcYourDetails.email || this.gcErrors.yourEmail) return false;
    if (this.gcPurchaseFor === 'someone') {
      if (!this.gcRecipient.firstName.trim() || !this.gcRecipient.lastName.trim()) return false;
      if (!this.gcRecipient.email || this.gcErrors.recipientEmail) return false;
    }
    return true;
  }

  get gcCanProceedPayment(): boolean {
    if (this.stripePublishableKey && isStripeEnabled()) {
      return stripeCardComplete();
    }
    const cn = (this.gcPaymentData.cardNumber || '').replace(/\s/g, '');
    if (cn.length < 15) return false;
    if (!(this.gcPaymentData.expiry || '').match(/^\d{2} \/ \d{2}$/)) return false;
    if ((this.gcPaymentData.cvc || '').length < 3) return false;
    return true;
  }

  /** Carry the buyer's name from the details step into the payment "Full name"
   * so we don't ask for it twice. Left editable; only fills when still blank. */
  prefillGcPaymentName(): void {
    if (this.gcPaymentData.fullName.trim()) return;
    const name = `${this.gcYourDetails.firstName} ${this.gcYourDetails.lastName}`.trim();
    if (name) this.gcPaymentData.fullName = name;
  }

  /** Capture the buyer as a client + a "pending purchase" the moment they hit
   * Continue, so an abandoned/stuck checkout leaves a reachable lead. Best
   * effort and non-blocking — never delays or blocks reaching the payment step;
   * only fires once per attempt. */
  recordPendingGiftCardPurchase(apiBase: string | undefined): void {
    if (this.gcPendingId) return;
    const base = apiBase ? normalizeBase(apiBase) : apiBase;
    if (!base || base === 'mock') return;
    const body = {
      amount: this.gcAmount,
      purchaseFor: this.gcPurchaseFor,
      yourDetails: this.gcYourDetails,
      recipient: this.gcRecipient,
      promotionId: this.gcSelectedPromotion ? this.gcSelectedPromotion.id : undefined,
    };
    fetch(`${base}/api/v1/gift-cards/purchase/pending/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.pendingId) this.gcPendingId = d.pendingId; })
      .catch(() => { /* lead capture is best-effort; never surface to the buyer */ });
  }

  openGiftCard(): void {
    this.gcSelectedPromotion = null;
    this.gcPurchaseFor = 'someone';
    this.gcAmount = '';
    this.gcYourDetails = { firstName: '', lastName: '', email: '', phone: '' };
    this.gcRecipient = { firstName: '', lastName: '', message: '', email: '', phone: '' };
    this.gcAgreedTerms = false;
    this.gcPaymentData = { fullName: '', country: 'US', address: '', cardNumber: '', expiry: '', cvc: '' };
    this.gcShowMissing = false;
    this.gcSubmitting = false;
    this.gcPayError = null;
    this.gcCardViewUrl = null;
    this.gcPendingId = null;
    this.gcRememberedFirstName = '';
    this.navigate('gc-details');
    this.prefillGcFromDevice();
    trackWidgetEvent('gift_card_open', { entryMode: this.entryMode });
  }

  selectGcPromotion(promoId: number): void {
    const promo = this.gcPromotions.find(p => p.id === promoId);
    if (!promo) return;
    this.gcSelectedPromotion = promo;
    this.gcAmount = promo.salePrice;
  }

  clearGcPromotion(): void {
    this.gcSelectedPromotion = null;
    this.gcAmount = '';
  }

  /** The business requires the gift card terms to be ticked and they aren't. */
  get gcTermsMissing(): boolean {
    const hasTerms = !!(this.gcConfig.disclaimer || (this.gcSelectedPromotion && this.gcConfig.promoDisclaimer));
    return !!(this.gcConfig.requireTermsAgreement && hasTerms && !this.gcAgreedTerms);
  }

  async submitGiftCardPayment(apiBase: string | undefined): Promise<void> {
    // Terms agreement gate (when the business requires it and terms exist).
    if (this.gcTermsMissing) {
      this.gcPayError = 'Please agree to the gift card terms to continue.';
      return;
    }
    // One purchase at a time. A second tap while the first is still with
    // Stripe used to run the whole thing again — two charges, two cards,
    // two sales (Sep 2026).
    if (this.gcSubmitting) return;
    this.gcSubmitting = true;
    this.gcPayError = null;
    const base = apiBase ? normalizeBase(apiBase) : apiBase;

    try {
      if (!base || base === 'mock') {
        await new Promise(resolve => setTimeout(resolve, TIMING.GC_MOCK_DELAY));
        if (Math.random() < 0.1) {
          throw new Error('Your card was declined. Please check your card details and try again.');
        }
        const mockCardId = Math.random().toString(36).slice(2, 10).toUpperCase();
        this.gcCardViewUrl = this.gcConfig.viewCardBaseUrl + mockCardId;
        this.step = 'gc-success';
      } else {
        // Tokenize card via Stripe if enabled
        let stripePaymentMethodId = '';
        if (this.stripePublishableKey && isStripeEnabled()) {
          stripePaymentMethodId = await stripeCreatePM({
            name: this.gcPaymentData.fullName,
          });
        }

        const purchaseBody: Record<string, unknown> = {
          amount: this.gcAmount,
          purchaseFor: this.gcPurchaseFor,
          yourDetails: this.gcYourDetails,
          recipient: this.gcRecipient,
          payment: this.gcPaymentData,
        };
        if (stripePaymentMethodId) {
          purchaseBody.stripePaymentMethodId = stripePaymentMethodId;
        }
        if (this.gcSelectedPromotion) {
          purchaseBody.promotionId = this.gcSelectedPromotion.id;
        }
        if (this.gcPendingId) {
          purchaseBody.pendingId = this.gcPendingId;
        }
        const resp = await fetch(`${base}/api/v1/gift-cards/purchase/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(purchaseBody),
        });
        if (!resp.ok) {
          const errData = await resp.json().catch(() => null);
          throw new Error(errData?.error || `Gift card purchase failed: ${resp.statusText}`);
        }
        const data = await resp.json();
        if (!data.success) throw new Error(data.error || 'Payment could not be processed.');
        this.gcCardViewUrl = data.cardViewUrl;
        this.step = 'gc-success';
        trackWidgetEvent('gift_card_purchase', {
          amount: Number(this.gcAmount) || 0,
          purchaseFor: this.gcPurchaseFor,
          promotionId: this.gcSelectedPromotion?.id ?? null,
          promotionName: this.gcSelectedPromotion?.name ?? '',
        });
        // Remember the buyer on this device, like a booking does.
        if (this.rememberOnDevice) {
          const d = this.gcYourDetails;
          saveRemembered(this.apiBase, { firstName: d.firstName, lastName: d.lastName, phone: d.phone, email: d.email, smsOpt: '' });
        }
      }
    } catch (err) {
      const raw = String((err as Error).message || '');
      // Stripe's "We could not retrieve data from the specified Element" means
      // nothing to a buyer (one asked us what it meant). Nothing was charged —
      // say so, and say what to do; the screen redraws with a fresh card field.
      this.gcPayError = isLostCardFieldError(raw)
        ? 'Sorry — we lost your card details before they were sent, so nothing was charged. Please enter your card again and tap Buy.'
        : (raw || 'Payment could not be processed. Please try again.');
      trackWidgetEvent('gift_card_error', { message: raw.slice(0, 120), diag: cardDiagnostics() });
    } finally {
      this.gcSubmitting = false;
    }
  }

  // ── Packages ──────────────────────────────────────────────────────────

  get pkgContactValid(): boolean {
    if (!this.pkgContact.firstName.trim() || !this.pkgContact.lastName.trim()) return false;
    const phone = (this.pkgContact.phone || '').replace(/\D/g, '');
    if (!phone || phone.length !== 10) return false;
    const email = this.pkgContact.email || '';
    if (!email || validateEmail(email)) return false;
    return true;
  }

  get pkgPaymentValid(): boolean {
    if (this.stripePublishableKey && isStripeEnabled()) {
      return stripeCardComplete();
    }
    const cn = (this.pkgPaymentData.cardNumber || '').replace(/\s/g, '');
    if (cn.length < 15) return false;
    if (!(this.pkgPaymentData.expiry || '').match(/^\d{2} \/ \d{2}$/)) return false;
    if ((this.pkgPaymentData.cvc || '').length < 3) return false;
    return true;
  }

  openPackages(): void {
    this.navigate('pkg-list');
  }

  pickPackage(id: number): void {
    const pkg = this.packages.find(p => p.id === id);
    if (!pkg) return;
    this.selectedPackage = pkg;
    this.pkgContact = { firstName: '', lastName: '', phone: '', email: '' };
    this.pkgPaymentData = { cardNumber: '', expiry: '', cvc: '' };
    this.pkgPayError = null;
    this.navigate('pkg-contact');
  }

  async submitPkgPayment(apiBase: string | undefined): Promise<void> {
    if (!this.selectedPackage || !this.pkgContactValid) return;
    this.pkgSubmitting = true;
    this.pkgPayError = null;
    const base = apiBase ? normalizeBase(apiBase) : apiBase;

    try {
      if (!base || base === 'mock') {
        await new Promise(r => setTimeout(r, TIMING.PKG_MOCK_DELAY));
        if (Math.random() < 0.05) throw new Error('Your card was declined.');
        this.step = 'pkg-success';
        return;
      }

      // Tokenize card via Stripe if enabled
      let stripePaymentMethodId = '';
      if (this.stripePublishableKey && isStripeEnabled()) {
        stripePaymentMethodId = await stripeCreatePM({
          name: `${this.pkgContact.firstName} ${this.pkgContact.lastName}`,
          email: this.pkgContact.email,
        });
      }

      const { firstName, lastName, phone, email } = this.pkgContact;
      const today = new Date().toISOString().split('T')[0];

      const searchRes = await fetch(`${base}/api/v1/clients/?q=${encodeURIComponent(email)}`);
      if (!searchRes.ok) throw new Error(`Client search failed: ${searchRes.statusText}`);
      const searchData = await searchRes.json();
      const existing = (Array.isArray(searchData) ? searchData : searchData.results || [])
        .find((c: Record<string, unknown>) => c.email === email);

      let clientId: number;
      let clientName: string;
      if (existing) {
        clientId = existing.id as number;
        clientName = existing.name as string;
      } else {
        const clientRes = await fetch(`${base}/api/v1/clients/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName, lastName, phone, email }),
        });
        if (!clientRes.ok) throw new Error(`Could not create client: ${clientRes.statusText}`);
        const newClient = await clientRes.json();
        clientId = newClient.id;
        clientName = `${firstName} ${lastName}`;
      }

      // If Stripe enabled, attach card to client first
      if (stripePaymentMethodId) {
        const cardRes = await fetch(`${base}/api/v1/clients/${clientId}/credit-cards/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stripePaymentMethodId }),
        });
        if (!cardRes.ok) throw new Error(`Could not attach card: ${cardRes.statusText}`);
      }

      const pkgRes = await fetch(`${base}/api/v1/client-packages/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: this.selectedPackage.id,
          clientId,
          clientName,
          purchaseDate: today,
        }),
      });
      if (!pkgRes.ok) throw new Error(`Package purchase failed: ${pkgRes.statusText}`);

      const saleRes = await fetch(`${base}/api/v1/sales/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientName,
          items: [{
            type: 'service',
            name: this.selectedPackage.name,
            price: this.selectedPackage.totalPrice,
            originalPrice: this.selectedPackage.totalPrice,
          }],
        }),
      });
      if (!saleRes.ok) throw new Error(`Could not create sale: ${saleRes.statusText}`);
      const sale = await saleRes.json();

      const closeRes = await fetch(`${base}/api/v1/sales/${sale.id}/close/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tip: 0, paymentMethod: 'credit_card' }),
      });
      if (!closeRes.ok) throw new Error(`Could not close sale: ${closeRes.statusText}`);

      this.step = 'pkg-success';
    } catch (err) {
      this.pkgPayError = (err as Error).message || 'Payment could not be processed.';
    } finally {
      this.pkgSubmitting = false;
    }
  }

  // ── Memberships ──────────────────────────────────────────────────────

  get memContactValid(): boolean {
    if (!this.memContact.firstName.trim() || !this.memContact.lastName.trim()) return false;
    const phone = (this.memContact.phone || '').replace(/\D/g, '');
    if (!phone || phone.length !== 10) return false;
    const email = this.memContact.email || '';
    if (!email || validateEmail(email)) return false;
    return true;
  }

  get memPaymentValid(): boolean {
    if (this.stripePublishableKey && isStripeEnabled()) {
      return stripeCardComplete();
    }
    const cn = (this.memPaymentData.cardNumber || '').replace(/\s/g, '');
    if (cn.length < 15) return false;
    if (!(this.memPaymentData.expiry || '').match(/^\d{2} \/ \d{2}$/)) return false;
    if ((this.memPaymentData.cvc || '').length < 3) return false;
    return true;
  }

  openMemberships(): void {
    this.navigate('mem-list');
  }

  pickMembership(id: number): void {
    const plan = this.membershipPlans.find(p => p.id === id);
    if (!plan) return;
    this.selectedMembershipPlan = plan;
    this.memContact = { firstName: '', lastName: '', phone: '', email: '' };
    this.memPaymentData = { cardNumber: '', expiry: '', cvc: '' };
    this.memPayError = null;
    this.navigate('mem-contact');
  }

  async submitMemPayment(apiBase: string | undefined): Promise<void> {
    if (!this.selectedMembershipPlan || !this.memContactValid) return;
    this.memSubmitting = true;
    this.memPayError = null;
    const base = apiBase ? normalizeBase(apiBase) : apiBase;

    try {
      if (!base || base === 'mock') {
        await new Promise(r => setTimeout(r, TIMING.PKG_MOCK_DELAY));
        if (Math.random() < 0.05) throw new Error('Your card was declined.');
        this.step = 'mem-success';
        return;
      }

      // Tokenize card via Stripe if enabled
      let stripePaymentMethodId = '';
      if (this.stripePublishableKey && isStripeEnabled()) {
        stripePaymentMethodId = await stripeCreatePM({
          name: `${this.memContact.firstName} ${this.memContact.lastName}`,
          email: this.memContact.email,
        });
      }

      const { firstName, lastName, phone, email } = this.memContact;

      const memBody: Record<string, unknown> = {
        planId: this.selectedMembershipPlan.id,
        clientPhone: phone,
        clientFirstName: firstName,
        clientLastName: lastName,
        clientEmail: email,
        // The tick is the agreement, and the server records what was agreed.
        // It also refuses without this, so the box can't be a UI formality.
        termsAccepted: this.portalPurchaseAgreed,
      };
      if (stripePaymentMethodId) {
        memBody.stripePaymentMethodId = stripePaymentMethodId;
      }

      await fetch(`${base}/api/v1/client-memberships/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(memBody),
      }).then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.error || 'Enrollment failed.'); });
        return r.json();
      });

      this.step = 'mem-success';
    } catch (err) {
      this.memPayError = (err as Error).message || 'Enrollment could not be processed.';
    } finally {
      this.memSubmitting = false;
    }
  }

  // ── Footer / breadcrumb getters ───────────────────────────────────────

  get showFooter(): boolean {
    return ['book-enhancements', 'book-contact', 'book-payment', 'gc-details', 'gc-payment', 'pkg-contact', 'pkg-payment', 'mem-contact', 'mem-payment'].includes(this.step);
  }

  get footerLabel(): string {
    if (this.step === 'book-contact') return this.bookingSubmitting ? 'Submitting…' : (this.depositDueAtSubmit > 0 ? 'Continue to Deposit' : 'Continue');
    if (this.step === 'book-payment') return this.depositPaying ? 'Processing…'
      : `Pay $${(this.deposit?.amount || 0).toFixed(2)}${this.deposit?.kind === 'products' ? '' : ' Deposit'}`;
    if (this.step === 'gc-details') return 'Continue to payment';
    if (this.step === 'gc-payment') return this.gcSubmitting ? 'Processing…' : 'Buy Gift Card';
    if (this.step === 'pkg-payment') return this.pkgSubmitting ? 'Processing…' : 'Complete Purchase';
    if (this.step === 'pkg-contact') return 'Continue to Payment';
    if (this.step === 'mem-payment') return this.memSubmitting ? 'Processing…' : 'Complete Enrollment';
    if (this.step === 'mem-contact') return 'Continue to Payment';
    return 'Continue';
  }

  get canProceed(): boolean {
    if (this.step === 'book-contact') { const hasConditions = !!(this.cancellationPolicyHtml && this.cancellationPolicyHtml.replace(/<[^>]*>/g, '').trim()) || (this.formConfig.notices || []).some(n => n.html && n.html.replace(/<[^>]*>/g, '').trim()); const errs = this.formErrors; const hasErrors = Object.keys(errs).length > 0; return (!hasConditions || this.agreedToPolicy) && !hasErrors && !this.bookingSubmitting && (this.groupSize <= 1 || this.guestsValid); }
    if (this.step === 'book-payment') return !!this.deposit && !this.depositPaying
      && (this.stripePublishableKey ? stripeCardComplete() : false)
      && (!this.deposit?.policy?.required || this.depositPolicyAgreed)
      && (!this.depositGcNumber.trim() || this.depositGcAgreed);
    // Always tappable: a tap with gaps explains what is missing instead of
    // a dead disabled button (see gcMissingDetails).
    if (this.step === 'gc-details') return true;
    if (this.step === 'gc-payment') return this.gcCanProceedPayment && !this.gcSubmitting;
    if (this.step === 'pkg-contact') return this.pkgContactValid;
    if (this.step === 'pkg-payment') return this.pkgPaymentValid && !this.pkgSubmitting;
    if (this.step === 'mem-contact') return this.memContactValid;
    if (this.step === 'mem-payment') return this.memPaymentValid && !this.memSubmitting;
    return true;
  }

  bcClass(section: string): string {
    const step = this.step;
    const serviceSteps = ['book-enhancements', 'book-provider'];
    const dateSteps = ['book-datetime'];
    const confirmSteps = ['book-contact', 'book-confirm', 'book-payment'];
    const doneSteps = ['book-success', 'book-pending-text'];
    if (section === 'service') return serviceSteps.includes(step) ? 'active' : (dateSteps.includes(step) || confirmSteps.includes(step) || doneSteps.includes(step)) ? 'done' : 'future';
    if (section === 'date') return dateSteps.includes(step) ? 'active' : (confirmSteps.includes(step) || doneSteps.includes(step)) ? 'done' : 'future';
    if (section === 'confirm') return confirmSteps.includes(step) ? 'active' : doneSteps.includes(step) ? 'done' : 'future';
    if (section === 'success') return doneSteps.includes(step) ? 'active' : 'future';
    return 'future';
  }

  // ── Book-for-other helpers ────────────────────────────────────────────

  get otherValid(): boolean {
    return !!(this.otherFirstName.trim() && this.otherLastName.trim() && this.responsibleAgreed);
  }

  get totalDuration(): number {
    if (!this.curr.service) return 0;
    return this.curr.service.duration + this.curr.selectedAddons.reduce((s, a) => s + (a.durationAdd || 0), 0);
  }

  /** Deposit for ONE booked item (0 when its service takes none). */
  private _itemDeposit(it: CurrentItem | BookingItem): number {
    const svc = 'service' in it ? (it as CurrentItem).service : (it as BookingItem).service;
    if (!svc || !svc.requirePaymentDeposit) return 0;
    const price = 'totalPrice' in it ? (it as CurrentItem).totalPrice : (it as BookingItem).totalPrice;
    if (svc.requireFullPayment) return price;
    if (svc.depositAmountType === 'percent') return Math.round(price * (svc.depositAmount || 0) / 100 * 100) / 100;
    return svc.depositAmount || 0;
  }

  private get _depositItems(): Array<CurrentItem | BookingItem> {
    const all = this.items;
    // A service a package / membership session covers owes no deposit — the
    // session is the hold (the server leaves it out the same way). An OFFER
    // only discounts: the deposit is on the discounted price, on the ONE
    // item it applies to.
    if (this.bookingCredit?.kind === 'promotion') {
      return all.map((it, i) => {
        const d = this.creditDiscountAt(i);
        return d > 0 ? ({ ...it, totalPrice: Math.max(0, Math.round((Number(it.totalPrice || 0) - d) * 100) / 100) } as CurrentItem | BookingItem) : it;
      });
    }
    return all.filter((_it, i) => !this.creditCoversAt(i));
  }

  /** Calculate total deposit required across all booking items. */
  get depositRequired(): number {
    return this._depositItems.reduce((t, it) => t + this._itemDeposit(it), 0);
  }

  /** What is owed when the client HAS a card on file: services that waive for
   *  card holders add nothing, the others still add theirs — the server's
   *  per-service rule (utils/deposits.py). */
  private get _depositOwedWithCard(): number {
    return this._depositItems.reduce((t, it) => {
      const svc = 'service' in it ? (it as CurrentItem).service : (it as BookingItem).service;
      return t + (svc && svc.waiveDepositWithCard ? 0 : this._itemDeposit(it));
    }, 0);
  }

  private get _hasCardOnFile(): boolean {
    if (this.client && this.wallet && this.wallet.creditCards && this.wallet.creditCards.length > 0) {
      if (this.wallet.creditCards.some(c => !!c.hasStripePaymentMethod)) return true;
    }
    return this.isKnownClient && !!this.knownClient!.cardOnFile;
  }

  /** Whether the WHOLE deposit is waived because the client has a card on file. */
  get depositWaivedWithCard(): boolean {
    if (this.depositRequired <= 0) return false;
    return this._hasCardOnFile && this._depositOwedWithCard <= 0;
  }

  /** The phone typed on the contact step matched a client on file. */
  get isKnownClient(): boolean {
    if (!this.knownClient) return false;
    return _digits(this.knownClient.phone) === _digits((this.formData.phone as string) || '');
  }

  /** Will this booking go to the deposit step? (What the contact step says
   * under the total — the server is the authority, this is the honest
   * preview: a returning client with a card on file owes only the deposits of
   * services that don't waive for card holders.) */
  get depositDueAtSubmit(): number {
    if (this.depositCollectionMethod === 'venmo') return 0;
    if (this.depositRequired <= 0) return 0;
    return this._hasCardOnFile ? this._depositOwedWithCard : this.depositRequired;
  }

  /** Enter the deposit step for a just-created booking. */
  /** A pickup of shop-cart products the client may pay for now. Same rule as
   *  the server (payment_links.retail_prepay_allowed): the booking is ONLY the
   *  retail pickup appointment, carries products, and cards are available. */
  get productPrepayOffered(): boolean {
    const pickup = this.retailPickupServiceId;
    if (!this.retailPrepayEnabled || !pickup || !this.stripePublishableKey) return false;
    if (!this.selectedProducts.length || !this.items.length) return false;
    return this.items.every((i: any) => Number(i.service?.id) === Number(pickup));
  }

  get productsTotal(): number {
    return this.selectedProducts.reduce((t: number, p: any) => t + (Number(p.price) || 0) * (Number(p.quantity) || 1), 0);
  }

  beginDepositStep(link: DepositLink): void {
    this.productsPrepaid = false;
    this.deposit = link;
    this.depositPaying = false;
    this.depositPayError = null;
    this.depositTextState = link.textedAt ? 'sent' : 'idle';
    this.step = 'book-payment';
  }

  // ── Booking payload ───────────────────────────────────────────────────

  /** `credit: {kind, id, groupId}` on the items the credit covers: a session on
   *  every covered item, an offer on the ONE item `creditItemIndex` names. */
  private _creditFor(it: { service: { id: number | string } | null; selectedAddons?: Array<{ id: string | number; price: number }> }, idx?: number): { credit?: { kind: string; id: number; groupId: number | string | null } } {
    const c = this.bookingCredit;
    if (!c) return {};
    if (c.kind === 'promotion') {
      if (idx === undefined || idx !== this.creditItemIndex) return {};
    } else if (!this.creditCovers(it.service, it)) return {};
    return { credit: { kind: c.kind, id: c.id, groupId: c.groupId } };
  }

  /** The portal payload's items — the whole cart, each at its chained start. */
  private _portalItems(): Array<Record<string, unknown>> {
    return this.items.map((it, idx) => ({
      serviceId: it.service.id,
      addonIds: it.selectedAddons.map(a => a.id),
      staffId: it.provider.type?.startsWith('filter') ? null : Number(it.provider.id),
      providerPreference: this._providerPreference(it.provider),
      totalPrice: it.totalPrice,
      startTime: this.itemStartTimes[idx] || this.selectedTime,
      durationMins: this._itemDuration(it),
      guestIndex: it.guestIndex ?? 0,
      ...this._creditFor(it, idx),
    }));
  }

  private _providerPreference(prov: { id: string | number; type?: string }): string {
    if (prov.type === 'filter-female') return 'female';
    if (prov.type === 'filter-male') return 'male';
    if (prov.type === 'filter-any') return 'any';
    if (prov.type === 'ai-assigned') return 'any';
    return 'requested';
  }

  buildBookingPayload(overrideClientId?: number): object {
    const payload = this._buildBookingPayload(overrideClientId) as Record<string, unknown>;
    if (this.existingHoldChoice) payload.existingHold = this.existingHoldChoice;
    return payload;
  }

  private _buildBookingPayload(overrideClientId?: number): object {
    // Portal logged-in flow (the cart, no contact form — the record is the contact)
    if (this.clientId && this.entryMode === 'portal') {
      if (this.bookForOther) {
        const p1: Record<string, unknown> = {
          clientId: overrideClientId || null,
          clientName: `${this.otherFirstName.trim()} ${this.otherLastName.trim()}`,
          bookedByClientId: this.clientId,
          bookedByClientName: this.client?.name || '',
          items: this._portalItems(),
          appointment: { date: this.selectedDate, time: this.selectedTime },
          // The server's 'book for someone else' resolution: the booker is
          // the signed-in client, the other person their own record WITH a
          // phone, else a placeholder (Tim, 2026-11-03 — sent as a plain client
          // before, so a blank phone made a record with no phone).
          client: {
            firstName: this.client?.firstName || '',
            lastName: this.client?.lastName || '',
            email: this.client?.email || '',
            phone: this.client?.phone || '',
            forOther: true,
            otherFirstName: this.otherFirstName.trim(),
            otherLastName: this.otherLastName.trim(),
            otherEmail: this.otherEmail.trim(),
            otherPhone: this.otherPhone.trim(),
            otherIsMinor: this.otherIsMinor,
            otherResponsibility: this.responsibleAgreed,
          },
        };
        if (this.selectedLocationId) p1.locationId = this.selectedLocationId;
        if (this.stripePaymentMethodId) p1.stripePaymentMethodId = this.stripePaymentMethodId;
        p1.source = 'portal';
        if (this.usesSameDay(this.selectedDate)) p1.sameDay = true;
        return p1;
      }
      const p2: Record<string, unknown> = {
        source: 'portal',
        clientId: this.clientId,
        clientName: this.client?.name || '',
        items: this._portalItems(),
        appointment: { date: this.selectedDate, time: this.selectedTime },
        client: {
          firstName: this.client?.firstName || '',
          lastName: this.client?.lastName || '',
          email: this.client?.email || '',
          phone: this.client?.phone || '',
        },
        // Marketing opt-in checkbox on the logged-in confirm screen (10DLC)
        smsMarketingOptIn: this.bkMarketingTexts,
      };
      if (this.selectedLocationId) p2.locationId = this.selectedLocationId;
      if (this.stripePaymentMethodId) p2.stripePaymentMethodId = this.stripePaymentMethodId;
      if (this.usesSameDay(this.selectedDate)) p2.sameDay = true;
      return p2;
    }

    // Public booking flow (multi-service, contact form, promo)
    const payload: Record<string, unknown> = {
      // Tells the backoffice this came through the public online-booking flow
      // (vs the logged-in client portal above). Server-side, staff requests are
      // always 'backoffice' regardless of what a payload claims.
      source: 'widget',
      items: this.items.map((it, idx) => ({
        serviceId: it.service.id,
        addonIds: it.selectedAddons.map(a => a.id),
        staffId: it.provider.type?.startsWith('filter') ? null : Number(it.provider.id),
        providerPreference: this._providerPreference(it.provider),
        totalPrice: it.totalPrice,
        startTime: this.itemStartTimes[idx] || this.selectedTime,
        durationMins: this._itemDuration(it),
        guestIndex: it.guestIndex ?? 0,
        ...this._creditFor(it, idx),
      })),
      appointment: { date: this.selectedDate, time: this.selectedTime },
      client: this.formData,
      // The conditions toggle, as a fact the server can RECORD — it's what
      // lets the deposit page skip re-showing the policy to this client.
      policyAgreed: this.agreedToPolicy || undefined,
      smsMarketingOptIn: this.bkMarketingTexts,
      promoCode: ((this.formData['promo'] as string) || '').trim(),
      promoOfferId: this.promoStatus === 'valid' ? (this.promoOffer?.offerId ?? null) : null,
      discountAmount: this.promoStatus === 'valid' ? (this.promoOffer?.discountAmount ?? 0) : 0,
    };

    // Today under "Open Today rules": the server re-validates against the
    // same-day engine, applies the same-day Offer itself and stamps
    // source widget_today (the same product, reached from the calendar).
    if (this.usesSameDay(this.selectedDate)) payload.sameDay = true;

    // If logged in via "Returning client?" flow, inject clientId
    if (this.clientId) {
      payload.clientId = this.clientId;
      payload.clientName = this.client?.name || '';
    }

    // Location
    if (this.selectedLocationId) {
      payload.locationId = this.selectedLocationId;
    }

    // Group booking: include guest details
    if (this.groupSize > 1 && this.guests.length > 0) {
      payload.guests = this.guests.filter(g => g && (g.firstName || g.lastName || g.phone || g.email));
    }

    // Include Stripe payment method for deposit if set
    if (this.stripePaymentMethodId) {
      payload.stripePaymentMethodId = this.stripePaymentMethodId;
    }

    // Waitlist
    if (this.waitlistMode) {
      payload.waitlist = true;
      payload.waitlistTimeBlock = this.waitlistTimeBlock;
    }

    // AI concierge conversation association
    if (this.aiConversationId) {
      payload.aiConversationId = this.aiConversationId;
    }

    // Checkout products
    if (this.selectedProducts.length > 0) {
      payload.checkoutProducts = this.selectedProducts.map((p: any) => ({
        productId: p.id, name: p.name, price: p.price, quantity: Math.max(1, Number(p.quantity) || 1),
      }));
    }
    if (this.productPrepayOffered && this.productsPayNow) payload.prepayProducts = true;

    return payload;
  }

  // ── Field dispatcher ──────────────────────────────────────────────────

  setField(field: string, value: unknown): void {
    // GC payment fields
    if (['gcFullName', 'gcCountry', 'gcAddress', 'gcCardNumber', 'gcExpiry', 'gcCvc'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.gcPaymentData> = {
        gcFullName: 'fullName', gcCountry: 'country', gcAddress: 'address',
        gcCardNumber: 'cardNumber', gcExpiry: 'expiry', gcCvc: 'cvc'
      };
      (this.gcPaymentData as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // GC your-details fields
    if (['gcYourFirstName', 'gcYourLastName', 'gcYourPhone', 'gcYourEmail'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.gcYourDetails> = {
        gcYourFirstName: 'firstName', gcYourLastName: 'lastName',
        gcYourPhone: 'phone', gcYourEmail: 'email'
      };
      (this.gcYourDetails as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // GC recipient fields
    if (['gcRecipientFirstName', 'gcRecipientLastName', 'gcRecipientMessage', 'gcRecipientEmail', 'gcRecipientPhone'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.gcRecipient> = {
        gcRecipientFirstName: 'firstName', gcRecipientLastName: 'lastName',
        gcRecipientMessage: 'message', gcRecipientEmail: 'email', gcRecipientPhone: 'phone'
      };
      (this.gcRecipient as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // GC amount
    if (field === 'gcAmount') { this.gcAmount = value as number | string; return; }
    // GC purchase-for
    if (field === 'gcPurchaseFor') { this.gcPurchaseFor = value as 'someone' | 'yourself'; return; }
    // GC terms agreement checkbox
    if (field === 'gcAgreedTerms') { this.gcAgreedTerms = !!value; return; }
    // Package contact fields
    if (['pkgFirstName', 'pkgLastName', 'pkgPhone', 'pkgEmail'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.pkgContact> = {
        pkgFirstName: 'firstName', pkgLastName: 'lastName',
        pkgPhone: 'phone', pkgEmail: 'email',
      };
      (this.pkgContact as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // Package payment fields
    if (['pkgCardNumber', 'pkgExpiry', 'pkgCvc'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.pkgPaymentData> = {
        pkgCardNumber: 'cardNumber', pkgExpiry: 'expiry', pkgCvc: 'cvc',
      };
      (this.pkgPaymentData as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // Membership contact fields
    if (['memFirstName', 'memLastName', 'memPhone', 'memEmail'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.memContact> = {
        memFirstName: 'firstName', memLastName: 'lastName',
        memPhone: 'phone', memEmail: 'email',
      };
      (this.memContact as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // Membership payment fields
    if (['memCardNumber', 'memExpiry', 'memCvc'].includes(field)) {
      const keyMap: Record<string, keyof typeof this.memPaymentData> = {
        memCardNumber: 'cardNumber', memExpiry: 'expiry', memCvc: 'cvc',
      };
      (this.memPaymentData as Record<string, unknown>)[keyMap[field]] = value;
      return;
    }
    // Registration fields
    if (field === 'regMarketingEmails') { this.regMarketingEmails = value as boolean; return; }
    if (field === 'regMarketingTexts') { this.regMarketingTexts = value as boolean; return; }
    if (field === 'bkMarketingTexts') { this.bkMarketingTexts = value as boolean; return; }
    // agreedToPolicy
    if (field === 'agreedToPolicy') { this.agreedToPolicy = value as boolean; return; }
    if (field === 'depositPolicyAgreed') { this.depositPolicyAgreed = !!value; return; }
    // Guest fields: guest1FirstName, guest1LastName, guest1Phone, guest1Email, etc.
    const guestMatch = field.match(/^guest(\d+)(FirstName|LastName|Phone|Email)$/);
    if (guestMatch) {
      const gi = parseInt(guestMatch[1], 10);
      const key = guestMatch[2].charAt(0).toLowerCase() + guestMatch[2].slice(1) as keyof GuestInfo;
      if (!this.guests[gi]) this.guests[gi] = { firstName: '', lastName: '', phone: '', email: '' };
      this.guests[gi][key] = value as string;
      return;
    }
    // Everything else goes to formData
    this.formData[field] = value;
  }

  // ── Time helpers ──────────────────────────────────────────────────────

  _parseTimeToMins(timeStr: string): number {
    const m = (timeStr || '').trim().match(/^(\d+):(\d+)\s*(am|pm)$/i);
    if (!m) return 0;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toLowerCase();
    if (ap === 'pm' && h !== 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    return h * 60 + min;
  }

  _minsToTimeStr(mins: number): string {
    const h24 = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    const ap = h24 >= 12 ? 'pm' : 'am';
    const h12 = h24 % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
  }

  _itemDuration(item: BookingItem): number {
    return item.service.duration + item.selectedAddons.reduce((s, a) => s + (a.durationAdd || 0), 0);
  }
}


function _digits(v: string): string { return (v || '').replace(/\D/g, '').slice(-10); }
